import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import './BuilderJS6.css';

// Load BuilderJS 6.0 CSS and JS files
const loadBuilderJS6 = () => {
  return new Promise((resolve, reject) => {
    // Check if BuilderJS is already loaded
    if (window.Builder) {
      resolve();
      return;
    }

    // Ensure Material Icons are loaded (required for widget icons and element controls)
    if (!document.querySelector('link[href*="Material+Icons"]')) {
      const materialIconsLink = document.createElement('link');
      materialIconsLink.rel = 'stylesheet';
      materialIconsLink.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(materialIconsLink);
      
      // Wait for font to load
      const fontLink = document.createElement('link');
      fontLink.rel = 'preconnect';
      fontLink.href = 'https://fonts.googleapis.com';
      document.head.appendChild(fontLink);
      
      const fontLink2 = document.createElement('link');
      fontLink2.rel = 'preconnect';
      fontLink2.href = 'https://fonts.gstatic.com';
      fontLink2.crossOrigin = 'anonymous';
      document.head.appendChild(fontLink2);
    }

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/builderjs6/builder.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = '/builderjs6/builder.js';
    script.onload = () => {
      if (window.Builder) {
        resolve();
      } else {
        reject(new Error('BuilderJS 6.0 failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load BuilderJS 6.0 script'));
    document.body.appendChild(script);
  });
};

const BuilderJS6 = ({ 
  initialData = null, 
  onSave = null, 
  onExport = null,
  themeUrl = '/themes/standard/Y1_c_1-2-1_Column_Layout_theme',
  viewMode = false
}) => {
  const mainContainerRef = useRef(null);
  const settingsContainerRef = useRef(null);
  const widgetsContainerRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const builderInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentLayout, setCurrentLayout] = useState('1 Column Layout');
  const [currentThemeName, setCurrentThemeName] = useState('1-2-1 Column Layout');
  const [deviceView, setDeviceView] = useState('desktop');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState('widgets'); // 'widgets' or 'settings'
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [currentThemeUrl, setCurrentThemeUrl] = useState(themeUrl);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [floatingModal, setFloatingModal] = useState({
    visible: false,
    position: { top: 0, left: 0 },
    elementId: null,
    elementName: null
  });
  const floatingModalRef = useRef(null);
  const floatingModalContainerRef = useRef(null);

  // Close dropdown and floating modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showThemeSelector) {
        const wrapper = document.querySelector('.builderjs6-theme-selector-wrapper');
        if (wrapper && !wrapper.contains(event.target)) {
          setShowThemeSelector(false);
        }
      }
      
      // Close floating modal when clicking outside - DISABLED
      // if (floatingModal.visible) {
      //   const modal = document.querySelector('.builderjs6-floating-modal');
      //   if (modal && !modal.contains(event.target)) {
      //     // Check if click is on an element (don't close if clicking another element)
      //     const clickedElement = event.target.closest('[builder-element]');
      //     if (!clickedElement) {
      //       setFloatingModal({ ...floatingModal, visible: false });
      //       if (builderInstanceRef.current && builderInstanceRef.current.unselect) {
      //         try {
      //           builderInstanceRef.current.unselect();
      //         } catch (e) {
      //           // Ignore
      //         }
      //       }
      //     }
      //   }
      // }
    };

    if (showThemeSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showThemeSelector]);

  useEffect(() => {
    let mounted = true;

    // Global error handler - wrapped in try-catch to prevent handler errors
    const errorHandler = (event) => {
      try {
        const error = event?.error || event?.reason;
        if (!error) return;
        
        const errorMessage = error?.message || error?.toString() || '';
        const errorStack = error?.stack || '';
        
        // Suppress BuilderJS-related errors (including frameElement and setPosSize)
        if (
          errorMessage.includes('frameElement') ||
          errorMessage.includes('setPosSize') ||
          errorMessage.includes('BaseElement') ||
          errorMessage.includes('innerHTML') ||
          errorMessage.includes('openTab') ||
          errorMessage.includes('styleString') ||
          errorMessage.includes('Theme mismatch') ||
          errorMessage.includes('RSSElement') ||
          errorMessage.includes('renderTemplate') ||
          errorMessage.includes('Cannot set properties of null') ||
          errorMessage.includes('Cannot read properties of null') ||
          errorMessage.includes('Cannot read properties of undefined') ||
          errorMessage.includes('firstChild') ||
          errorMessage.includes('addEventListener') ||
          errorMessage.includes('renderElementControls') ||
          errorMessage.includes('unselect') ||
          errorMessage.includes('selectElement') ||
          errorMessage.includes('updateElement') ||
          errorMessage.includes('setFormat') ||
          errorMessage.includes('stopImmediatePropagation') ||
          (errorMessage.includes('builder') && errorMessage.includes('not defined')) ||
          errorStack.includes('BaseElement') ||
          errorStack.includes('BuilderBundle') ||
          errorStack.includes('Builder.js') ||
          errorStack.includes('UIManager.js') ||
          errorStack.includes('RSSElement') ||
          errorStack.includes('renderTemplate') ||
          errorStack.includes('H1Element.js') ||
          errorStack.includes('PElement.js') ||
          errorStack.includes('H2Element.js') ||
          errorStack.includes('H3Element.js') ||
          errorStack.includes('TextElement.js') ||
          errorStack.includes('renderElementControls') ||
          errorStack.includes('unselect') ||
          errorStack.includes('selectElement') ||
          errorStack.includes('updateElement') ||
          errorStack.includes('setFormat')
        ) {
          // Suppress these errors - they're non-critical BuilderJS internal errors
          
          // Safely call event methods only if they exist - don't throw errors
          // Use optional chaining and try-catch for each method separately
          try {
            event?.preventDefault?.();
          } catch (e) {
            // Silently ignore
          }
          try {
            event?.stopPropagation?.();
          } catch (e) {
            // Silently ignore
          }
          try {
            event?.stopImmediatePropagation?.();
          } catch (e) {
            // Silently ignore - this is the main issue
          }
          
          return false;
        }
      } catch (handlerError) {
        // If error handler itself throws an error, just ignore it and don't rethrow
        return false;
      }
    };

    window.addEventListener('error', errorHandler, true);
    window.addEventListener('unhandledrejection', (event) => {
      // Handle promise rejections - check if it's a BuilderJS error we should suppress
      const error = event.reason;
      if (error) {
        const errorMessage = error?.message || error?.toString() || '';
        const errorStack = error?.stack || '';
        
        // Check if this is a BuilderJS error we should suppress
        if (
          errorMessage.includes('Theme mismatch') ||
          errorMessage.includes('frameElement') ||
          errorMessage.includes('setPosSize') ||
          errorMessage.includes('BaseElement') ||
          errorStack.includes('Builder.js') ||
          errorStack.includes('BuilderBundle')
        ) {
          // Suppress this promise rejection
          event.preventDefault(); // Prevent default unhandled rejection behavior
          return;
        }
      }
      
      // For other rejections, create a proper event-like object and pass to errorHandler
      const fakeEvent = {
        error: event.reason,
        preventDefault: () => {},
        stopPropagation: () => {},
        stopImmediatePropagation: () => {}
      };
      errorHandler(fakeEvent);
    });

    // Override window.alert to suppress BuilderJS alerts (template not found, theme mismatch, etc.)
    const originalAlert = window.alert;
    window.alert = function(message) {
      // Suppress BuilderJS-related alerts - check multiple patterns
      if (typeof message === 'string') {
        const msg = message.toLowerCase();
        if (
          (msg.includes('template') && msg.includes('not found')) ||
          (msg.includes('template') && msg.includes('page')) ||
          (msg.includes('page') && msg.includes('not found')) ||
          msg.includes('template "page" not found') ||
          msg.includes('template \'page\' not found') ||
          msg.includes('theme mismatch') ||
          msg.includes('expected') && msg.includes('but got') ||
          (msg.includes('theme') && msg.includes('mismatch')) ||
          (msg.includes('parsing data') && msg.includes('theme'))
        ) {
          // Suppress this alert
          return;
        }
      }
      // For other alerts, use original behavior
      return originalAlert.call(window, message);
    };
    // Store reference for cleanup
    window._builderjs6OriginalAlert = originalAlert;

    const initializeBuilder = async () => {
      try {
        setIsLoading(true);
        
        // Wait for containers to be ready (reduced retries for faster loading)
        // In view mode, only mainContainer is needed (sidebar is hidden)
        let retries = 0;
        const requiredContainers = viewMode 
          ? [mainContainerRef.current]
          : [mainContainerRef.current, settingsContainerRef.current, widgetsContainerRef.current];
        
        // Reduced retries from 30 to 10 (1 second max instead of 3 seconds)
        while (requiredContainers.some(ref => !ref) && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!mainContainerRef.current) {
          // Don't show error immediately, containers might still be mounting
          setTimeout(() => {
            if (!mainContainerRef.current) {
              toast.error('Failed to initialize: Main container not found');
              setIsLoading(false);
            }
          }, 1000);
          return;
        }
        
        // In view mode, settings and widgets containers are not needed
        if (!viewMode && (!settingsContainerRef.current || !widgetsContainerRef.current)) {
          setTimeout(() => {
            if (!settingsContainerRef.current || !widgetsContainerRef.current) {
              toast.error('Failed to initialize: Sidebar containers not found');
              setIsLoading(false);
            }
          }, 1000);
          return;
        }

        await loadBuilderJS6();

        if (!mounted) return;

        // Set container IDs
        if (!mainContainerRef.current.id) {
          mainContainerRef.current.id = 'builderjs6-main';
        }
        // In view mode, settings and widgets containers are not needed (sidebar is hidden)
        if (!viewMode) {
          if (settingsContainerRef.current && !settingsContainerRef.current.id) {
            settingsContainerRef.current.id = 'builderjs6-settings';
          }
          if (widgetsContainerRef.current && !widgetsContainerRef.current.id) {
            widgetsContainerRef.current.id = 'builderjs6-widgets';
          }
        }

        // CRITICAL: Set window.builder placeholder BEFORE creating builder
        // Widgets load asynchronously and try to access builder immediately
        // Create a placeholder that will be replaced with the real builder
        const builderPlaceholder = {
          _isPlaceholder: true,
          widgetsBox: {
            addWidget: function() {
              // Placeholder - builder not ready
            },
            render: function() {
              // Placeholder - builder not ready
            }
          }
        };
        window.builder = builderPlaceholder;
        // Also set on globalThis and self for maximum compatibility
        // eslint-disable-next-line no-undef
        if (typeof globalThis !== 'undefined') {
          // eslint-disable-next-line no-undef
          globalThis.builder = builderPlaceholder;
        }
        // eslint-disable-next-line no-restricted-globals
        if (typeof self !== 'undefined') {
          // eslint-disable-next-line no-restricted-globals
          self.builder = builderPlaceholder;
        }

        // Wait for DOM to be ready
        await new Promise(resolve => setTimeout(resolve, 300));

        const builderConfig = {
          mainContainer: `#${mainContainerRef.current.id}`,
          themeUrl: '/themes/standard/',
        };
        
        // In view mode, settings and widgets containers are not needed (sidebar is hidden)
        if (!viewMode) {
          if (settingsContainerRef.current && settingsContainerRef.current.id) {
            builderConfig.settingContainer = `#${settingsContainerRef.current.id}`;
          }
          if (widgetsContainerRef.current && widgetsContainerRef.current.id) {
            builderConfig.widgetsContainer = `#${widgetsContainerRef.current.id}`;
          }
        }
        
        const builder = new window.Builder(builderConfig);
        
        // CRITICAL: Set window.builder and builderInstanceRef IMMEDIATELY after creation
        // This must be done before any widgets try to access it
        window.builder = builder;
        builderInstanceRef.current = builder;
        
        // Hook into BuilderJS's selectElement method to show floating modal
        const originalSelectElement = builder.selectElement.bind(builder);
        builder.selectElement = function(element) {
          // Call original method first
          originalSelectElement(element);
          
          // Show floating modal after element is selected
          setTimeout(() => {
            if (!mainContainerRef.current || !builder || !element) return;
            
            // Get the selected element's DOM node
            const selectedElement = element.domNode;
            if (!selectedElement) return;
            
            console.log('Element selected via BuilderJS selectElement:', element);
            
            // Get element ID from the element object or DOM
            const elementId = selectedElement.getAttribute('builder-element') || 
                             selectedElement.getAttribute('data-element-id') ||
                             selectedElement.id ||
                             element.id ||
                             element.getClassName();
            
            console.log('Selected element ID:', elementId);
            
            if (elementId || element) {
                // Get element position for floating modal - use getBoundingClientRect for viewport coordinates
                const rect = selectedElement.getBoundingClientRect();
                
                // Use viewport coordinates directly (getBoundingClientRect gives viewport-relative positions)
                let top = rect.top;
                let left = rect.left;
                
                const modalHeight = 80; // Smaller height for compact modal
                const modalWidth = Math.min(Math.max(rect.width, 350), 450); // Match element width, min 350px, max 450px
                
                // Position above element (like original BuilderJS)
                top = top - modalHeight - 2; // Position 2px above element
                
                // Keep modal within viewport bounds
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                
                // If not enough space above, position below
                if (top < 10) {
                  top = rect.bottom + 2;
                }
                
                // Ensure modal stays within viewport
                left = Math.max(10, Math.min(left, viewportWidth - modalWidth - 10));
                top = Math.max(10, Math.min(top, viewportHeight - modalHeight - 10));
                
                const elementName = (elementId || element.getClassName()).replace('Element', '') || 'Element';
                
                console.log('Showing floating modal at:', { top, left, width: modalWidth, elementId, elementName });
                
                // Show floating modal - DISABLED
                // setFloatingModal({
                //   visible: true,
                //   position: { top, left, width: modalWidth },
                //   elementId: elementId || element.getClassName(),
                //   elementName: elementName,
                //   element: element // Store element reference
                // });
                
                // After modal is rendered, copy element controls - DISABLED
              setTimeout(() => {
                // Copy actions box (from BuilderJS's actions-box-inner)
                const actionsBox = document.querySelector('.actions-box-inner');
                const floatingActions = document.getElementById('builderjs6-floating-actions');
                
                if (actionsBox && floatingActions) {
                  const actionsClone = actionsBox.cloneNode(true);
                  actionsClone.style.cssText = 'display: flex !important; align-items: center !important; gap: 4px !important; flex-wrap: nowrap !important; background: transparent !important; border: none !important; padding: 0 !important;';
                  
                  // Fix button styles
                  const buttons = actionsClone.querySelectorAll('button');
                  buttons.forEach(btn => {
                    btn.style.cssText += 'background: transparent !important; border: none !important; padding: 4px 6px !important; display: inline-flex !important; align-items: center !important; justify-content: center !important;';
                  });
                  
                  floatingActions.innerHTML = '';
                  floatingActions.appendChild(actionsClone);
                  
                  // Re-attach event listeners
                  const actionButtons = floatingActions.querySelectorAll('button');
                  const originalButtons = actionsBox.querySelectorAll('button');
                  actionButtons.forEach((btn, index) => {
                    if (originalButtons[index]) {
                      btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        originalButtons[index].click();
                      });
                    }
                  });
                }
                
                // Copy main control (like text input for PElement) to modal content
                const settingsContainer = settingsContainerRef.current;
                const floatingSettings = document.getElementById('builderjs6-floating-settings');
                
                if (settingsContainer && floatingSettings) {
                  // Find the main control (usually the first text control or input)
                  const mainControl = settingsContainer.querySelector('[data-control="settings-controls-container"]')?.firstElementChild?.nextElementSibling ||
                                     settingsContainer.querySelector('textarea') ||
                                     settingsContainer.querySelector('input[type="text"]') ||
                                     settingsContainer.querySelector('.form-control');
                  
                  if (mainControl) {
                    // Clone the main control
                    const controlClone = mainControl.cloneNode(true);
                    controlClone.style.cssText = 'width: 100% !important; margin: 0 !important;';
                    
                    floatingSettings.innerHTML = '';
                    floatingSettings.appendChild(controlClone);
                    
                    // Sync changes back to original control
                    if (controlClone.tagName === 'TEXTAREA' || controlClone.tagName === 'INPUT') {
                      controlClone.addEventListener('input', (e) => {
                        if (mainControl.tagName === controlClone.tagName) {
                          mainControl.value = e.target.value;
                          // Trigger change event on original
                          const event = new Event('input', { bubbles: true });
                          mainControl.dispatchEvent(event);
                        }
                      });
                    }
                  } else {
                    // If no main control found, show element class name
                    floatingSettings.innerHTML = `<div style="padding: 12px; color: #666; font-size: 14px;">${elementName} Settings</div>`;
                  }
                }
                
                if (window._builderjs6FixFloatingIcons) {
                  window._builderjs6FixFloatingIcons();
                  setTimeout(() => window._builderjs6FixFloatingIcons(), 50);
                  setTimeout(() => window._builderjs6FixFloatingIcons(), 150);
                }
              }, 150);
            }
          }, 50);
        };
        
        // Also update globalThis and self
        // eslint-disable-next-line no-undef
        if (typeof globalThis !== 'undefined') {
          // eslint-disable-next-line no-undef
          globalThis.builder = builder;
        }
        // eslint-disable-next-line no-restricted-globals
        if (typeof self !== 'undefined') {
          // eslint-disable-next-line no-restricted-globals
          self.builder = builder;
        }
        
        // Ensure widgetsBox exists (some BuilderJS versions initialize it lazily)
        // This is critical because widgets load asynchronously and try to call addWidget
        if (!builder.widgetsBox) {
          // Create a placeholder widgetsBox that queues widget additions
          const widgetQueue = [];
          builder.widgetsBox = {
            _queue: widgetQueue,
            _realWidgetsBox: null,
            addWidget: function(...args) {
              if (this._realWidgetsBox && typeof this._realWidgetsBox.addWidget === 'function') {
                return this._realWidgetsBox.addWidget.apply(this._realWidgetsBox, args);
              } else {
                // Queue the widget addition for later
                this._queue.push({ method: 'addWidget', args });
              }
            },
            render: function() {
              // Ensure container exists before rendering
              if (!this._container && widgetsContainerRef.current) {
                this._container = widgetsContainerRef.current;
              }
              
              if (this._realWidgetsBox && typeof this._realWidgetsBox.render === 'function') {
                // Ensure real widgetsBox has container
                if (!this._realWidgetsBox._container && this._container) {
                  this._realWidgetsBox._container = this._container;
                }
                // Only render if container exists
                if (this._container) {
                  return this._realWidgetsBox.render.apply(this._realWidgetsBox, arguments);
                } else {
                  return;
                }
              } else if (this._container) {
                // If no real widgetsBox but we have container, just return
                return;
              }
            }
          };
          
          // Set container reference
          if (widgetsContainerRef.current) {
            builder.widgetsBox._container = widgetsContainerRef.current;
          }
          
          // Try to get the real widgetsBox after a short delay
          setTimeout(() => {
            if (builder.widgetsBox && builder.widgetsBox._queue && !builder.widgetsBox._realWidgetsBox) {
              // Check if builder now has a real widgetsBox
              if (builder.widgetsBox && typeof builder.widgetsBox.addWidget === 'function' && !builder.widgetsBox._isPlaceholder) {
                // The real widgetsBox is now available
                builder.widgetsBox._realWidgetsBox = builder.widgetsBox;
                
                // Ensure container is set
                if (!builder.widgetsBox._container && widgetsContainerRef.current) {
                  builder.widgetsBox._container = widgetsContainerRef.current;
                }
                
                // Process queued widgets
                if (builder.widgetsBox._queue && builder.widgetsBox._queue.length > 0) {
                  builder.widgetsBox._queue.forEach(item => {
                    if (builder.widgetsBox._realWidgetsBox[item.method] && typeof builder.widgetsBox._realWidgetsBox[item.method] === 'function') {
                      builder.widgetsBox._realWidgetsBox[item.method].apply(builder.widgetsBox._realWidgetsBox, item.args);
                    }
                  });
                  builder.widgetsBox._queue = [];
                }
              }
            }
          }, 100);
        }
        
        // Ensure widgetsBox always has a container reference and proper render method
        if (builder.widgetsBox) {
          // Set container reference
          if (!builder.widgetsBox._container && widgetsContainerRef.current) {
            builder.widgetsBox._container = widgetsContainerRef.current;
          }
          // Also set container property if BuilderJS uses it
          if (!builder.widgetsBox.container && widgetsContainerRef.current) {
            builder.widgetsBox.container = widgetsContainerRef.current;
          }
          
          // Override render to ensure container exists before appendChild
          if (builder.widgetsBox.render) {
            const originalRender = builder.widgetsBox.render.bind(builder.widgetsBox);
            builder.widgetsBox.render = function() {
              // Ensure container exists
              if (!this._container && widgetsContainerRef.current) {
                this._container = widgetsContainerRef.current;
              }
              // Also check if builder has container property
              if (!this._container && this.container) {
                this._container = this.container;
              }
              // If still no container, try to get from DOM
              if (!this._container) {
                const containerEl = document.getElementById('builderjs6-widgets');
                if (containerEl) {
                  this._container = containerEl;
                }
              }
              
              if (this._container) {
                return originalRender.apply(this, arguments);
              } else {
                return;
              }
            };
          }
        }

        // CRITICAL: Override BuilderJS's internal settingContainer getter to always return our container
        // In view mode, settings container is not needed (sidebar is hidden)
        let ourSettingsContainer = null;
        if (!viewMode && settingsContainerRef.current && settingsContainerRef.current.id) {
          ourSettingsContainer = document.getElementById(settingsContainerRef.current.id);
        }
        if (ourSettingsContainer && builder) {
          // Store reference to our container
          builder._ourSettingsContainer = ourSettingsContainer;
          builder._ourSettingsContainerId = settingsContainerRef.current.id;
          
          // Override the settingContainer property if BuilderJS uses it
          try {
            // Try to override the getter
            const originalDescriptor = Object.getOwnPropertyDescriptor(builder, 'settingContainer') ||
                                     Object.getOwnPropertyDescriptor(Object.getPrototypeOf(builder), 'settingContainer');
            
            if (originalDescriptor && originalDescriptor.get) {
              // If there's a getter, override it
              Object.defineProperty(builder, 'settingContainer', {
                get: function() {
                  const container = document.getElementById(this._ourSettingsContainerId) || this._ourSettingsContainer;
                  if (container) {
                    container.style.display = 'block';
                    container.style.visibility = 'visible';
                    container.style.width = '100%';
                    container.style.height = '100%';
                  }
                  return container;
                },
                set: function(value) {
                  // Don't allow override, keep our container
                  this._internalSettingContainer = value;
                },
                configurable: true,
                enumerable: true
              });
            } else {
              // No getter, just set the property directly
              builder.settingContainer = ourSettingsContainer;
            }
          } catch (e) {
            // If override fails, just set it directly
            builder.settingContainer = ourSettingsContainer;
          }
        }

        // Override builder.load to maintain width after theme load
        if (builder.load) {
          const originalLoad = builder.load.bind(builder);
          builder.load = function(...args) {
            const result = originalLoad.apply(this, args);
            
            // Function to fix width for container and iframes
            const fixWidth = () => {
              if (mainContainerRef.current) {
                const container = mainContainerRef.current;
                container.style.setProperty('width', '100%', 'important');
                container.style.setProperty('min-width', '100%', 'important');
                container.style.setProperty('max-width', '100%', 'important');
                
                // Also fix iframe width if BuilderJS uses iframe
                const iframes = container.querySelectorAll('iframe');
                iframes.forEach(iframe => {
                  iframe.style.setProperty('width', '100%', 'important');
                  iframe.style.setProperty('min-width', '100%', 'important');
                  iframe.style.setProperty('max-width', '100%', 'important');
                });
              }
            };
            
            // Ensure width is maintained after load - multiple times
            setTimeout(fixWidth, 0);
            setTimeout(fixWidth, 100);
            setTimeout(fixWidth, 500);
            setTimeout(fixWidth, 1000);
            
            return result;
          };
        }

        // Create settingsTabManager that opens/closes the settings panel
        const settingsTabManager = {
          openTab: (tabName) => {
            // Open settings panel when tab is opened
            setIsSettingsOpen(true);
            setActivePanel('settings');
            
            // Ensure settings container is visible
            const settingsContent = document.querySelector('.builderjs6-settings-content');
            if (settingsContent) {
              settingsContent.style.display = 'flex';
              settingsContent.style.visibility = 'visible';
              settingsContent.classList.add('active');
              settingsContent.classList.remove('hidden');
            }
            
            // Ensure settings container exists in DOM and is ready
            const settingsContainer = document.getElementById(settingsContainerRef.current.id);
            if (settingsContainer) {
              settingsContainer.style.display = 'block';
              settingsContainer.style.visibility = 'visible';
              settingsContainer.style.width = '100%';
              settingsContainer.style.height = '100%';
              settingsContainer.style.overflow = 'auto';
              
              // Ensure container is empty and ready for BuilderJS to render
              // Don't clear if it already has content
              if (!settingsContainer.innerHTML.trim() || settingsContainer.innerHTML.length < 10) {
                settingsContainer.innerHTML = '';
              }
            }
          },
          closeTab: () => {
            // Close settings panel and show widgets
            setIsSettingsOpen(false);
            setActivePanel('widgets');
          },
          getCurrentTab: () => {
            return activePanel === 'settings' ? 'settings' : null;
          },
          switchTab: (tabName) => {
            // Switch to settings panel
            setIsSettingsOpen(true);
            setActivePanel('settings');
            
            const settingsContent = document.querySelector('.builderjs6-settings-content');
            if (settingsContent) {
              settingsContent.style.display = 'flex';
              settingsContent.style.visibility = 'visible';
              settingsContent.classList.add('active');
              settingsContent.classList.remove('hidden');
            }
            
            const settingsContainer = document.getElementById(settingsContainerRef.current.id);
            if (settingsContainer) {
              settingsContainer.style.display = 'block';
              settingsContainer.style.visibility = 'visible';
            }
          },
        };

        // CRITICAL: Assign settingsTabManager to builder - try multiple ways
        // This MUST be done before any element interactions
        builder.settingsTabManager = settingsTabManager;
        if (builder.setSettingsTabManager) {
          builder.setSettingsTabManager(settingsTabManager);
        }
        window.sidebarTabManager = settingsTabManager;
        window.builder = builder;
        
        // Use Object.defineProperty to ensure settingsTabManager is always accessible
        // This ensures BuilderJS can always find it when calling openTab
        try {
          Object.defineProperty(builder, 'settingsTabManager', {
            get: function() {
              return window.sidebarTabManager || settingsTabManager;
            },
            set: function(value) {
              window.sidebarTabManager = value;
            },
            configurable: true,
            enumerable: true
          });
        } catch (e) {
          // Fallback: just set it directly
          builder.settingsTabManager = settingsTabManager;
        }
        
        // Also try to set it on the builder prototype if possible
        try {
          if (builder.constructor && builder.constructor.prototype) {
            Object.defineProperty(builder.constructor.prototype, 'settingsTabManager', {
              get: function() {
                return window.sidebarTabManager || settingsTabManager;
              },
              set: function(value) {
                window.sidebarTabManager = value;
              },
              configurable: true,
              enumerable: true
            });
          }
        } catch (e) {
          // Ignore prototype errors
        }

        // Override methods with null checks
        if (builder.unselect) {
          const originalUnselect = builder.unselect.bind(builder);
          builder.unselect = function() {
            try {
              // CRITICAL: Ensure settingsTabManager is set BEFORE calling original
              if (!this.settingsTabManager) {
                this.settingsTabManager = settingsTabManager;
              }
              if (!window.sidebarTabManager) {
                window.sidebarTabManager = settingsTabManager;
              }
              
              const result = originalUnselect();
              // Close settings panel when element is unselected
              setIsSettingsOpen(false);
              setActivePanel('widgets');
              // Close floating modal
              setFloatingModal(prev => ({ ...prev, visible: false }));
              return result;
            } catch (e) {
              // Suppress errors
            }
          };
        }

        if (builder.selectElement) {
          const originalSelectElement = builder.selectElement.bind(builder);
          builder.selectElement = function(id) {
            // In view mode, prevent element selection
            if (viewMode) {
              return null;
            }
            
            try {
              // CRITICAL: Ensure settingsTabManager is set BEFORE calling originalSelectElement
              // This is how BuilderJS expects it - it will call openTab internally
              if (!this.settingsTabManager) {
                this.settingsTabManager = settingsTabManager;
              }
              if (!window.sidebarTabManager) {
                window.sidebarTabManager = settingsTabManager;
              }
              
              // CRITICAL: Open settings panel FIRST before anything else
              setIsSettingsOpen(true);
              setActivePanel('settings');
              
              // Force settings panel to be visible immediately
              const forceOpenSettings = () => {
                const settingsContent = document.querySelector('.builderjs6-settings-content');
                if (settingsContent) {
                  settingsContent.classList.remove('hidden');
                  settingsContent.classList.add('active');
                  settingsContent.style.display = 'flex';
                  settingsContent.style.visibility = 'visible';
                  settingsContent.style.opacity = '1';
                  settingsContent.style.height = '100%';
                }
                
                // Ensure settings container is visible
                const settingsContainer = settingsContainerRef.current;
                if (settingsContainer) {
                  settingsContainer.style.display = 'block';
                  settingsContainer.style.visibility = 'visible';
                  settingsContainer.style.width = '100%';
                  settingsContainer.style.height = '100%';
                  settingsContainer.style.overflow = 'auto';
                  settingsContainer.style.opacity = '1';
                }
              };
              
              // Force immediately
              forceOpenSettings();
              setTimeout(forceOpenSettings, 0);
              setTimeout(forceOpenSettings, 50);
              
              // Now select the element - BuilderJS will call openTab internally via settingsTabManager
              const result = originalSelectElement(id);
              
              // CRITICAL: Explicitly call renderElementControls after selectElement
              // BuilderJS might not call it automatically, so we ensure it's called
              setTimeout(() => {
                try {
                  if (!this.renderElementControls) return;
                  
                  // Try multiple methods to render controls
                  // Method 1: Try with the element ID
                  if (id) {
                    this.renderElementControls(id);
                  } 
                  // Method 2: Try with the result object if it's an element
                  else if (result && typeof result === 'object') {
                    this.renderElementControls(result);
                  } 
                  // Method 3: Try to find element in DOM
                  else if (id && mainContainerRef.current) {
                    const element = mainContainerRef.current.querySelector(`[builder-element="${id}"]`) ||
                                   mainContainerRef.current.querySelector(`[data-element-id="${id}"]`) ||
                                   mainContainerRef.current.querySelector(`#${id}`);
                    if (element) {
                      this.renderElementControls(element);
                    } else {
                      // Method 4: Try without arguments - BuilderJS might get element internally
                      this.renderElementControls();
                    }
                  } else {
                    // Method 5: Try without arguments
                    this.renderElementControls();
                  }
                } catch (renderErr) {
                  // If renderElementControls fails, try once more after a delay
                  setTimeout(() => {
                    try {
                      if (id && this.renderElementControls) {
                        const element = mainContainerRef.current?.querySelector(`[builder-element="${id}"]`) ||
                                       mainContainerRef.current?.querySelector(`[data-element-id="${id}"]`);
                        if (element) {
                          this.renderElementControls(element);
                        } else {
                          this.renderElementControls(id);
                        }
                      }
                    } catch (err) {
                      // Suppress errors
                    }
                  }, 100);
                }
              }, 50);
              
              return result;
            } catch (e) {
              // Suppress errors but still open panel
              setIsSettingsOpen(true);
              setActivePanel('settings');
            }
          };
        }

        if (builder.renderElementControls) {
          const originalRender = builder.renderElementControls.bind(builder);
          builder.renderElementControls = function(element) {
            try {
              // CRITICAL: Ensure settingsTabManager is set BEFORE calling original
              // BuilderJS may call openTab internally during renderElementControls
              if (!this.settingsTabManager) {
                this.settingsTabManager = settingsTabManager;
              }
              if (!window.sidebarTabManager) {
                window.sidebarTabManager = settingsTabManager;
              }
              
              if (!element) {
                return null;
              }
              
              // CRITICAL: Ensure settings container exists and is ready BEFORE calling original
              // Try multiple ways to get the container
              let settingsContainer = null;
              
              // Method 1: Use builder's stored reference (most reliable)
              if (this._ourSettingsContainer && this._ourSettingsContainer.parentElement) {
                settingsContainer = this._ourSettingsContainer;
              }
              
              // Method 2: Get by ID from stored ID
              if (!settingsContainer && this._ourSettingsContainerId) {
                settingsContainer = document.getElementById(this._ourSettingsContainerId);
              }
              
              // Method 3: Get by ID from ref
              if (!settingsContainer && settingsContainerRef.current && settingsContainerRef.current.id) {
                settingsContainer = document.getElementById(settingsContainerRef.current.id);
              }
              
              // Method 4: Get directly from ref if it's in DOM
              if (!settingsContainer && settingsContainerRef.current && settingsContainerRef.current.parentElement) {
                settingsContainer = settingsContainerRef.current;
              }
              
              // Method 5: Try to find by class
              if (!settingsContainer) {
                settingsContainer = document.querySelector('.builderjs6-settings-container') ||
                                   document.querySelector('#builderjs6-settings');
              }
              
              if (!settingsContainer) {
                // Container not found - wait and retry once
                setTimeout(() => {
                  const container = this._ourSettingsContainer || 
                                   document.getElementById(this._ourSettingsContainerId || 'builderjs6-settings');
                  if (container && this.renderElementControls) {
                    this.renderElementControls(element);
                  }
                }, 100);
                return null;
              }
              
              // CRITICAL: Force BuilderJS to use our container
              // Override builder's settingContainer property so BuilderJS internal code finds it
              if (this.settingContainer !== settingsContainer) {
                this.settingContainer = settingsContainer;
              }
              
              // Ensure container is visible and has proper styling BEFORE rendering
              settingsContainer.style.display = 'block';
              settingsContainer.style.visibility = 'visible';
              settingsContainer.style.width = '100%';
              settingsContainer.style.height = '100%';
              settingsContainer.style.overflow = 'auto';
              settingsContainer.style.padding = '0';
              settingsContainer.style.margin = '0';
              
              // Open settings panel FIRST
              setIsSettingsOpen(true);
              setActivePanel('settings');
              
              // Call openTab to ensure BuilderJS knows to render
              if (this.settingsTabManager && this.settingsTabManager.openTab) {
                this.settingsTabManager.openTab();
              }
              
              // Ensure settings content wrapper is visible
              const settingsContent = document.querySelector('.builderjs6-settings-content');
              if (settingsContent) {
                settingsContent.style.display = 'flex';
                settingsContent.style.visibility = 'visible';
                settingsContent.classList.add('active');
                settingsContent.classList.remove('hidden');
              }
              
              // CRITICAL: Ensure BuilderJS can find the container
              // BuilderJS might be looking for container using a selector string
              // Make sure the container has the ID BuilderJS expects
              if (settingsContainer.id !== settingsContainerRef.current.id) {
                settingsContainer.id = settingsContainerRef.current.id;
              }
              
              // Also ensure BuilderJS's internal reference is set
              if (this.settingContainer !== settingsContainer) {
                this.settingContainer = settingsContainer;
              }
              
              // Try to override BuilderJS's internal container getter if it exists
              try {
                // Check if BuilderJS has a method to get settings container
                if (this.getSettingContainer && typeof this.getSettingContainer === 'function') {
                  const originalGetSettingContainer = this.getSettingContainer.bind(this);
                  this.getSettingContainer = function() {
                    return settingsContainer;
                  };
                }
              } catch (e) {
                // Ignore
              }
              
              // Ensure container has content area (BuilderJS might need this structure)
              // Don't clear if it already has content - BuilderJS might be appending
              if (!settingsContainer.innerHTML.trim() || settingsContainer.innerHTML.length < 10) {
                settingsContainer.innerHTML = '<div class="builderjs6-settings-content"></div>';
              }
              
              // Call original renderElementControls - BuilderJS will populate the container
              const result = originalRender.apply(this, arguments);
              
              // After rendering, copy to floating modal and fix icons
              setTimeout(() => {
                const floatingContainer = document.getElementById('builderjs6-floating-settings');
                const floatingHeader = document.querySelector('.builderjs6-floating-modal-header');
                
                if (floatingContainer && settingsContainer && settingsContainer.innerHTML.trim()) {
                  floatingContainer.innerHTML = settingsContainer.innerHTML;
                  
                  // Fix icons in floating modal content
                  fixFloatingModalIcons();
                }
                
                // Also check if BuilderJS rendered controls in the header area
                if (floatingHeader) {
                  // Look for element controls that should be in header
                  const mainControls = mainContainerRef.current?.querySelector('[class*="element-control"]');
                  if (mainControls) {
                    // Clone controls to header (right side)
                    const controlsClone = mainControls.cloneNode(true);
                    controlsClone.style.cssText = 'display: flex !important; align-items: center !important; gap: 4px !important; margin-left: auto !important;';
                    floatingHeader.appendChild(controlsClone);
                    fixFloatingModalIcons();
                  }
                }
              }, 100);
              
              return result;
            } catch (e) {
              // Suppress all errors
              setIsSettingsOpen(true);
              setActivePanel('settings');
              return null;
            }
          };
        }

        if (builder.renderTemplate) {
          const originalRenderTemplate = builder.renderTemplate.bind(builder);
          builder.renderTemplate = function(template, data) {
            try {
              if (!data) {
                data = {};
              }
              if (!data.styleString) {
                data = { ...data, styleString: '' };
              }
              // If template is 'Page' and not found, return empty string instead of throwing
              if (template === 'Page' || template === 'page') {
                try {
                  return originalRenderTemplate.call(this, template, data);
                } catch (e) {
                  // If Page template not found, return empty string
                  return '';
                }
              }
              return originalRenderTemplate.call(this, template, data);
            } catch (e) {
              // Suppress errors - return empty string for missing templates
              return '';
            }
          };
        } else {
          // If renderTemplate doesn't exist, create a stub
          builder.renderTemplate = function(template, data) {
            return '';
          };
        }

        window.builder = builder;
        builderInstanceRef.current = builder;

        // Override YoutubeWidget icon if it exists
        if (window.YoutubeWidget && window.YoutubeWidget.prototype) {
          const originalGetIcon = window.YoutubeWidget.prototype.getIcon;
          window.YoutubeWidget.prototype.getIcon = function() {
            if (originalGetIcon && originalGetIcon.call(this) === 'youtube_activity') {
              return 'play_circle'; // Use valid Material Icon
            }
            return originalGetIcon ? originalGetIcon.call(this) : 'play_circle';
          };
        }

        // Override scrollIntoView to prevent unwanted scrolling when expanding widget groups
        const originalScrollIntoView = Element.prototype.scrollIntoView;
        Element.prototype.scrollIntoView = function(options) {
          // Only prevent scroll if it's from widget group expansion
          if (this.closest && this.closest('.widgets-group')) {
            // Don't scroll, just return
            return;
          }
          // For other elements, use original behavior
          return originalScrollIntoView.call(this, options);
        };

        // Set up sidebarTabManager if it exists
        if (builder.settingsTabManager && !window.sidebarTabManager) {
          window.sidebarTabManager = builder.settingsTabManager;
        }

        // Fix icons in floating modal - more aggressive approach
        const fixFloatingModalIcons = () => {
          const floatingModal = document.querySelector('.builderjs6-floating-modal');
          if (!floatingModal) return;
          
          const iconMap = {
            'arrow_circle_up': 'arrow_upward',
            'content_copy': 'content_copy',
            'delete': 'delete',
            'remove_selection': 'close'
          };
          
          // Helper function to fix icons in any container
          const fixIconsInContainer = (container) => {
            if (!container) return;
            
            // Strategy 1: Find text nodes and replace
            const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
            const textNodes = [];
            let node;
            while (node = walker.nextNode()) {
              const text = node.textContent.trim();
              if (iconMap[text] && node.parentNode) {
                const parent = node.parentElement;
                // Skip if already converted
                if (parent && (parent.classList.contains('material-icons') || parent.querySelector('.material-icons'))) {
                  continue;
                }
                textNodes.push({ node, text, parent });
              }
            }
            
            textNodes.forEach(({ node, text, parent }) => {
              const iconSpan = document.createElement('span');
              iconSpan.className = 'material-icons';
              iconSpan.textContent = iconMap[text];
              iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #666 !important; display: inline-block !important; line-height: 1 !important;';
              try {
                parent.replaceChild(iconSpan, node);
              } catch (e) {
                if (parent.textContent.trim() === text) {
                  parent.innerHTML = iconSpan.outerHTML;
                }
              }
            });
            
            // Strategy 2: Find all elements and check text content
            const allElements = container.querySelectorAll('*');
            allElements.forEach(el => {
              // Skip if already converted
              if (el.classList.contains('material-icons') || el.querySelector('.material-icons')) {
                return;
              }
              
              const text = el.textContent.trim();
              const innerHTML = el.innerHTML.trim();
              
              Object.keys(iconMap).forEach(iconText => {
                if (text === iconText || innerHTML === iconText) {
                  // Only convert if it's a button, span, div, or anchor
                  if (el.tagName === 'BUTTON' || el.tagName === 'SPAN' || el.tagName === 'A' || el.tagName === 'DIV') {
                    const iconSpan = document.createElement('span');
                    iconSpan.className = 'material-icons';
                    iconSpan.textContent = iconMap[iconText];
                    iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #666 !important; display: inline-block !important; line-height: 1 !important;';
                    el.innerHTML = '';
                    el.appendChild(iconSpan);
                    
                    el.style.cssText = 'display: inline-flex !important; align-items: center !important; justify-content: center !important; padding: 4px 6px !important; cursor: pointer !important; min-width: 28px !important; min-height: 28px !important; background: transparent !important; border: none !important;';
                  }
                }
              });
            });
          };
          
          // Fix icons in header actions area
          const actionsArea = floatingModal.querySelector('#builderjs6-floating-actions');
          if (actionsArea) {
            fixIconsInContainer(actionsArea);
            
            // Ensure all buttons/spans are in a flex row
            if (actionsArea.children.length > 0) {
              actionsArea.style.cssText = 'display: flex !important; align-items: center !important; justify-content: flex-end !important; gap: 4px !important; flex-wrap: nowrap !important; margin-left: auto !important;';
              Array.from(actionsArea.children).forEach(child => {
                if (child.tagName === 'BUTTON' || child.tagName === 'SPAN' || child.tagName === 'A' || child.tagName === 'DIV') {
                  child.style.cssText += 'display: inline-flex !important; align-items: center !important; justify-content: center !important; flex-shrink: 0 !important;';
                }
              });
            }
          }
          
          // ALSO fix icons in the ENTIRE floating modal (for dropdown menus, etc.)
          // This will catch any icon text anywhere in the modal
          fixIconsInContainer(floatingModal);
        };
        
        // Store globally for access
        window._builderjs6FixFloatingIcons = fixFloatingModalIcons;

        // Fix element control icons - convert text to Material Icons
        const fixElementControlIcons = () => {
          // Search entire document, not just mainContainer
          const searchContainers = [document.body];
          if (mainContainerRef.current) {
            searchContainers.push(mainContainerRef.current);
          }
          
          // Also check iframe content if BuilderJS uses iframe
          const iframes = document.querySelectorAll('iframe');
          iframes.forEach(iframe => {
            try {
              if (iframe.contentDocument && iframe.contentDocument.body) {
                searchContainers.push(iframe.contentDocument.body);
              }
            } catch (e) {
              // Cross-origin iframe, skip
            }
          });
          
          if (searchContainers.length === 0) return;
          
          // Check if Material Icons font is loaded
          if (!document.fonts || !document.fonts.check) {
            // Fallback: just try anyway
          } else {
            try {
              const fontLoaded = document.fonts.check('18px "Material Icons"');
              if (!fontLoaded) {
                // Font not loaded yet, wait a bit
                setTimeout(fixElementControlIcons, 200);
                return;
              }
            } catch (e) {
              // Font check failed, continue anyway
            }
          }
          
          const iconMap = {
            'arrow_circle_up': 'arrow_upward',
            'content_copy': 'content_copy',
            'delete': 'delete',
            'remove_selection': 'close'
          };
          
          let totalConverted = 0;
          
          // Process each container
          searchContainers.forEach(mainContainer => {
            if (!mainContainer) return;
            
            let convertedCount = 0;

          // Strategy 1: Find ALL text nodes and replace them - NO CONDITIONS
          const walker = document.createTreeWalker(mainContainer, NodeFilter.SHOW_TEXT, null);
          const textNodes = [];
          let node;
          while (node = walker.nextNode()) {
            const text = node.textContent.trim();
            if (iconMap[text] && node.parentNode) {
              const parent = node.parentElement;
              if (parent && !parent.classList.contains('material-icons') && !parent.querySelector('.material-icons')) {
                // Convert ANY matching text, no conditions
                textNodes.push({ node, text, parent });
              }
            }
          }

          // Replace text nodes with Material Icons
          textNodes.forEach(({ node, text, parent }) => {
            const iconSpan = document.createElement('span');
            iconSpan.className = 'material-icons';
            iconSpan.textContent = iconMap[text];
            iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
            try {
              parent.replaceChild(iconSpan, node);
              convertedCount++;
            } catch (e) {
              // If replace fails, try innerHTML
              if (parent.textContent.trim() === text) {
                parent.innerHTML = iconSpan.outerHTML;
                convertedCount++;
              } else {
                // Try to replace just the text content
                parent.textContent = parent.textContent.replace(text, '');
                parent.appendChild(iconSpan);
                convertedCount++;
              }
            }
          });

          // Strategy 2: Find ALL elements and check for icon text - NO CONDITIONS, CONVERT EVERYTHING
          const allElements = mainContainer.querySelectorAll('*');
          allElements.forEach(el => {
            // Skip if already converted
            if (el.classList.contains('material-icons') || el.querySelector('.material-icons')) {
              return;
            }
            
            const text = el.textContent.trim();
            const innerHTML = el.innerHTML.trim();
            
            // Check if element contains icon text - convert ANY match
            Object.keys(iconMap).forEach(iconText => {
              // Match exact text
              if (text === iconText || innerHTML === iconText) {
                // Convert ANY element that matches, no conditions
                const iconSpan = document.createElement('span');
                iconSpan.className = 'material-icons';
                iconSpan.textContent = iconMap[iconText];
                iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
                
                // Clear and replace
                el.innerHTML = '';
                el.appendChild(iconSpan);
                convertedCount++;
                
                if (el.tagName === 'BUTTON' || el.tagName === 'SPAN' || el.tagName === 'A' || el.tagName === 'DIV') {
                  el.style.cssText += 'display: inline-flex !important; align-items: center !important; justify-content: center !important; padding: 4px 8px !important; cursor: pointer !important; min-width: 24px !important; min-height: 24px !important;';
                }
              }
            });
          });
          
          // Strategy 3: Find text nodes directly in modals - ULTRA AGGRESSIVE, NO CONDITIONS
          const modalSelectors = [
            '[class*="element-control"]',
            '[class*="control-bar"]',
            '[class*="element-overlay"]',
            '[class*="element-toolbar"]',
            'div[style*="position: fixed"]',
            'div[style*="position:absolute"]',
            'div[style*="position: fixed"]',
            'div[style*="z-index"]'
          ];
          
          modalSelectors.forEach(selector => {
            try {
              const modals = mainContainer.querySelectorAll(selector);
              modals.forEach(modal => {
                const walker = document.createTreeWalker(modal, NodeFilter.SHOW_TEXT, null);
                let textNode;
                while (textNode = walker.nextNode()) {
                  const text = textNode.textContent.trim();
                  if (iconMap[text] && textNode.parentNode) {
                    const parent = textNode.parentElement;
                    if (parent && !parent.classList.contains('material-icons') && !parent.querySelector('.material-icons')) {
                      const iconSpan = document.createElement('span');
                      iconSpan.className = 'material-icons';
                      iconSpan.textContent = iconMap[text];
                      iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
                      try {
                        parent.replaceChild(iconSpan, textNode);
                      } catch (e) {
                        if (parent.textContent.trim() === text) {
                          parent.innerHTML = iconSpan.outerHTML;
                        } else {
                          // Force replace
                          parent.textContent = parent.textContent.replace(text, '');
                          parent.appendChild(iconSpan);
                        }
                      }
                    }
                  }
                }
              });
            } catch (e) {
              // Ignore selector errors
            }
          });
          
          // Strategy 4: Direct text search and replace - MOST AGGRESSIVE
          // Search for text nodes containing icon text anywhere in the container
          Object.keys(iconMap).forEach(iconText => {
            // Use TreeWalker to find all text nodes
            const walker = document.createTreeWalker(
              mainContainer,
              NodeFilter.SHOW_TEXT,
              {
                acceptNode: function(node) {
                  const text = node.textContent.trim();
                  if (text === iconText) {
                    return NodeFilter.FILTER_ACCEPT;
                  }
                  return NodeFilter.FILTER_REJECT;
                }
              }
            );
            
            let textNode;
            while (textNode = walker.nextNode()) {
              const parent = textNode.parentElement;
              if (parent && !parent.classList.contains('material-icons') && !parent.querySelector('.material-icons')) {
                const iconSpan = document.createElement('span');
                iconSpan.className = 'material-icons';
                iconSpan.textContent = iconMap[iconText];
                iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
                try {
                  parent.replaceChild(iconSpan, textNode);
                  convertedCount++;
                } catch (e) {
                  // Force replace
                  try {
                    parent.textContent = parent.textContent.replace(iconText, '');
                    parent.appendChild(iconSpan);
                    convertedCount++;
                  } catch (e2) {
                    // Last resort: replace innerHTML
                    if (parent.textContent.trim() === iconText) {
                      parent.innerHTML = iconSpan.outerHTML;
                      convertedCount++;
                    }
                  }
                }
              }
            }
          });
          
            // Debug: Log if we found any icons to convert
            if (convertedCount > 0) {
              totalConverted += convertedCount;
            }
          });
          
          // Debug: Log total conversions
          if (totalConverted > 0) {
            console.log(`[BuilderJS6] Converted ${totalConverted} icon text(s) to Material Icons`);
          }
        };
        
        // Separate function to search ENTIRE document body - most aggressive
        const fixIconsInEntireDocument = () => {
          const iconMap = {
            'arrow_circle_up': 'arrow_upward',
            'content_copy': 'content_copy',
            'delete': 'delete',
            'remove_selection': 'close'
          };
          
          // Search entire document body for text nodes
          const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
          const textNodes = [];
          let node;
          while (node = walker.nextNode()) {
            const text = node.textContent.trim();
            if (iconMap[text] && node.parentNode) {
              const parent = node.parentElement;
              if (parent && !parent.classList.contains('material-icons') && !parent.querySelector('.material-icons')) {
                textNodes.push({ node, text, parent });
              }
            }
          }
          
          // Replace all found text nodes
          textNodes.forEach(({ node, text, parent }) => {
            const iconSpan = document.createElement('span');
            iconSpan.className = 'material-icons';
            iconSpan.textContent = iconMap[text];
            iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
            try {
              parent.replaceChild(iconSpan, node);
            } catch (e) {
              if (parent.textContent.trim() === text) {
                parent.innerHTML = iconSpan.outerHTML;
              } else {
                parent.textContent = parent.textContent.replace(text, '');
                parent.appendChild(iconSpan);
              }
            }
          });
          
          // Also search all elements in document
          document.querySelectorAll('*').forEach(el => {
            if (el.classList.contains('material-icons') || el.querySelector('.material-icons')) {
              return;
            }
            const text = el.textContent.trim();
            if (iconMap[text] && (el.tagName === 'BUTTON' || el.tagName === 'SPAN' || el.tagName === 'A' || el.tagName === 'DIV')) {
              const iconSpan = document.createElement('span');
              iconSpan.className = 'material-icons';
              iconSpan.textContent = iconMap[text];
              iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
              el.innerHTML = '';
              el.appendChild(iconSpan);
              el.style.cssText += 'display: inline-flex !important; align-items: center !important; justify-content: center !important; padding: 4px 8px !important; cursor: pointer !important; min-width: 24px !important; min-height: 24px !important;';
            }
          });
        };
        
        // Run on entire document immediately and frequently
        setTimeout(fixIconsInEntireDocument, 50);
        setTimeout(fixIconsInEntireDocument, 100);
        setTimeout(fixIconsInEntireDocument, 200);
        const documentIconInterval = setInterval(fixIconsInEntireDocument, 150);
        window._builderjs6DocumentIconInterval = documentIconInterval;

        // Run immediately and repeatedly - more frequent
        setTimeout(fixElementControlIcons, 50);
        setTimeout(fixElementControlIcons, 100);
        setTimeout(fixElementControlIcons, 200);
        setTimeout(fixElementControlIcons, 300);
        setTimeout(fixElementControlIcons, 500);
        setTimeout(fixElementControlIcons, 800);
        setTimeout(fixElementControlIcons, 1200);
        setTimeout(fixElementControlIcons, 2000);
        
        // Also run floating modal icon fix periodically
        const floatingIconInterval = setInterval(() => {
          if (document.querySelector('.builderjs6-floating-modal')) {
            if (window._builderjs6FixFloatingIcons) {
              window._builderjs6FixFloatingIcons();
            }
          }
        }, 500);
        
        // Store for cleanup
        window._builderjs6FloatingIconInterval = floatingIconInterval;
        
        // Set up MutationObserver to fix icons when controls are added - more aggressive
        const iconObserver = new MutationObserver((mutations) => {
          let shouldFix = false;
          mutations.forEach(mutation => {
            if (mutation.addedNodes.length > 0 || mutation.type === 'characterData') {
              shouldFix = true;
            }
          });
          if (shouldFix) {
            // Run immediately and with multiple delays
            fixElementControlIcons();
            fixIconsInEntireDocument();
            setTimeout(() => {
              fixElementControlIcons();
              fixIconsInEntireDocument();
            }, 10);
            setTimeout(() => {
              fixElementControlIcons();
              fixIconsInEntireDocument();
            }, 50);
            setTimeout(() => {
              fixElementControlIcons();
              fixIconsInEntireDocument();
            }, 100);
            // Also fix floating modal icons
            if (window._builderjs6FixFloatingIcons) {
              setTimeout(() => window._builderjs6FixFloatingIcons(), 100);
            }
          }
        });
        
        // Also observe entire document body for changes
        const documentIconObserver = new MutationObserver(() => {
          fixIconsInEntireDocument();
        });
        
        documentIconObserver.observe(document.body, {
          childList: true,
          subtree: true,
          characterData: true
        });
        window._builderjs6DocumentIconObserver = documentIconObserver;
        
        if (mainContainerRef.current) {
          iconObserver.observe(mainContainerRef.current, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['style', 'class']
          });
        }

        // Also set up interval as backup - very frequent
        const iconInterval = setInterval(fixElementControlIcons, 100);
        window._builderjs6IconInterval = iconInterval;

        // Function to apply consistent styling to BuilderJS element modals
        const styleElementModals = () => {
          if (!mainContainerRef.current) return;
          
          const iconMap = {
            'arrow_circle_up': 'arrow_upward',
            'content_copy': 'content_copy',
            'delete': 'delete',
            'remove_selection': 'close'
          };
          
          // Find all BuilderJS element control modals/panels
          const selectors = [
            '[class*="element-control"]',
            '[class*="control-bar"]',
            '[class*="element-overlay"]',
            '[class*="element-toolbar"]',
            'div[style*="position: fixed"]'
          ];
          
          selectors.forEach(selector => {
            const modals = mainContainerRef.current.querySelectorAll(selector);
            modals.forEach(modal => {
              // Skip if already styled or is our custom modal
              if (modal.classList.contains('builderjs6-floating-modal') || 
                  modal.closest('.builderjs6-floating-modal')) {
                return;
              }
              
              // Apply consistent styling
              modal.style.cssText += `
                background: #ffffff !important;
                border-radius: 8px !important;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
                border: 1px solid #e0e0e0 !important;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
              `;
              
              // Style header (first child div with dark background)
              const header = modal.querySelector('div:first-child');
              if (header && (header.style.backgroundColor.includes('rgb(51') || 
                            header.style.backgroundColor.includes('rgb(74') ||
                            header.textContent.match(/Element$/))) {
                header.style.cssText += `
                  background: #4a5568 !important;
                  padding: 10px 14px !important;
                  border-radius: 8px 8px 0 0 !important;
                  color: #ffffff !important;
                  font-weight: 600 !important;
                  font-size: 14px !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: space-between !important;
                  flex-wrap: nowrap !important;
                  overflow: hidden !important;
                  white-space: nowrap !important;
                `;
                
                // Make sure element name doesn't wrap
                const elementName = header.querySelector('*:first-child');
                if (elementName) {
                  elementName.style.cssText += `
                    white-space: nowrap !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    flex-shrink: 0 !important;
                    margin-right: 12px !important;
                  `;
                }
                
                // Find action buttons/icons in the modal (they might be in second div or anywhere)
                const allDivs = modal.querySelectorAll('div');
                let actionButtons = [];
                let actionsContainer = null;
                
                // Look for action buttons in ANY div (including header and all children)
                for (let i = 0; i < allDivs.length; i++) {
                  const div = allDivs[i];
                  // Skip header div
                  if (div === header) continue;
                  
                  const buttons = div.querySelectorAll('button, span, a, div, *');
                  buttons.forEach(btn => {
                    const text = btn.textContent.trim();
                    // Check if it's an action button by text content
                    if (iconMap[text] || 
                        text === 'arrow_circle_up' || text === 'content_copy' || 
                        text === 'delete' || text === 'remove_selection' ||
                        btn.textContent.includes('arrow_circle_up') || 
                        btn.textContent.includes('content_copy') || 
                        btn.textContent.includes('delete') ||
                        btn.textContent.includes('remove_selection')) {
                      // Skip if already in header
                      if (btn.closest('div:first-child') === header) {
                        return;
                      }
                      actionButtons.push(btn);
                      if (!actionsContainer) {
                        actionsContainer = div;
                      }
                    }
                  });
                }
                
                // Also search in entire modal for buttons with icon text
                const allElements = modal.querySelectorAll('*');
                allElements.forEach(el => {
                  const text = el.textContent.trim();
                  if (iconMap[text] && (el.tagName === 'BUTTON' || el.tagName === 'SPAN' || el.tagName === 'A' || el.tagName === 'DIV')) {
                    // Skip if already in header or already found
                    if (el.closest('div:first-child') === header || actionButtons.includes(el)) {
                      return;
                    }
                    actionButtons.push(el);
                    if (!actionsContainer && el.parentElement) {
                      actionsContainer = el.parentElement;
                    }
                  }
                });
                
                // If we found action buttons, move them to header
                if (actionButtons.length > 0) {
                  // Create actions container in header if it doesn't exist
                  let headerActions = header.querySelector('.builderjs6-header-actions');
                  if (!headerActions) {
                    headerActions = document.createElement('div');
                    headerActions.className = 'builderjs6-header-actions';
                    headerActions.style.cssText = 'display: flex !important; align-items: center !important; gap: 4px !important; margin-left: auto !important; flex-shrink: 0 !important; flex-wrap: nowrap !important; white-space: nowrap !important;';
                    header.appendChild(headerActions);
                  }
                  
                  // Clear existing cloned buttons to avoid duplicates
                  const existingButtons = headerActions.querySelectorAll('button, span, a, div');
                  existingButtons.forEach(existingBtn => {
                    if (existingBtn.classList.contains('builderjs6-cloned-action')) {
                      existingBtn.remove();
                    }
                  });
                  
                  // Move action buttons to header
                  actionButtons.forEach(btn => {
                    // Skip if already in header
                    if (btn.closest('div:first-child') === header) {
                      return;
                    }
                    
                    // Clone button to preserve event listeners
                    const clonedBtn = btn.cloneNode(true);
                    clonedBtn.classList.add('builderjs6-cloned-action');
                    clonedBtn.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; padding: 6px !important; border-radius: 4px !important; background: transparent !important; border: none !important; cursor: pointer !important; min-width: 28px !important; min-height: 28px !important; margin: 0 2px !important; transition: all 0.2s !important;';
                    
                    // Convert text to icon
                    const text = clonedBtn.textContent.trim();
                    if (iconMap[text]) {
                      clonedBtn.innerHTML = '';
                      const iconSpan = document.createElement('span');
                      iconSpan.className = 'material-icons';
                      iconSpan.textContent = iconMap[text];
                      iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important;';
                      clonedBtn.appendChild(iconSpan);
                    } else {
                      // If text doesn't match exactly, try to find icon text in innerHTML
                      const innerText = clonedBtn.innerText || clonedBtn.textContent || '';
                      Object.keys(iconMap).forEach(iconText => {
                        if (innerText.includes(iconText)) {
                          clonedBtn.innerHTML = '';
                          const iconSpan = document.createElement('span');
                          iconSpan.className = 'material-icons';
                          iconSpan.textContent = iconMap[iconText];
                          iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important;';
                          clonedBtn.appendChild(iconSpan);
                        }
                      });
                    }
                    
                    // Add click handler to trigger original button
                    clonedBtn.addEventListener('click', (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Trigger original button click
                      if (btn.click) {
                        btn.click();
                      } else if (btn.onclick) {
                        btn.onclick(e);
                      }
                      // Also try to find and trigger any event listeners
                      const clickEvent = new MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        view: window
                      });
                      btn.dispatchEvent(clickEvent);
                    });
                    
                    headerActions.appendChild(clonedBtn);
                    
                    // Hide original button
                    btn.style.display = 'none';
                  });
                  
                  // Hide the actions container if it's empty now
                  if (actionsContainer && actionButtons.length > 0) {
                    const remainingButtons = actionsContainer.querySelectorAll('button, span, a, div');
                    let hasVisibleButtons = false;
                    remainingButtons.forEach(b => {
                      if (b.style.display !== 'none' && b.textContent.trim()) {
                        hasVisibleButtons = true;
                      }
                    });
                    if (!hasVisibleButtons) {
                      actionsContainer.style.display = 'none';
                    }
                  }
                }
                
                // Convert icon text to Material Icons in header (for any remaining text)
                const walker = document.createTreeWalker(header, NodeFilter.SHOW_TEXT, null);
                let textNode;
                while (textNode = walker.nextNode()) {
                  const text = textNode.textContent.trim();
                  if (iconMap[text] && textNode.parentNode) {
                    const parent = textNode.parentElement;
                    if (parent && !parent.classList.contains('material-icons') && !parent.querySelector('.material-icons') && !parent.classList.contains('builderjs6-cloned-action')) {
                      const iconSpan = document.createElement('span');
                      iconSpan.className = 'material-icons';
                      iconSpan.textContent = iconMap[text];
                      iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
                      try {
                        parent.replaceChild(iconSpan, textNode);
                      } catch (e) {
                        if (parent.textContent.trim() === text) {
                          parent.innerHTML = iconSpan.outerHTML;
                        }
                      }
                    }
                  }
                }
                
                // Also check all elements in header
                header.querySelectorAll('*').forEach(el => {
                  if (el.classList.contains('material-icons') || el.querySelector('.material-icons') || el.classList.contains('builderjs6-cloned-action')) {
                    return;
                  }
                  const text = el.textContent.trim();
                  if (iconMap[text] && (el.tagName === 'BUTTON' || el.tagName === 'SPAN' || el.tagName === 'A' || el.tagName === 'DIV')) {
                    const iconSpan = document.createElement('span');
                    iconSpan.className = 'material-icons';
                    iconSpan.textContent = iconMap[text];
                    iconSpan.style.cssText = 'font-family: "Material Icons" !important; font-size: 18px !important; color: #fff !important; display: inline-block !important; line-height: 1 !important;';
                    el.innerHTML = '';
                    el.appendChild(iconSpan);
                    el.style.cssText += 'display: inline-flex !important; align-items: center !important; justify-content: center !important; padding: 4px 8px !important; cursor: pointer !important; min-width: 24px !important; min-height: 24px !important;';
                  }
                });
              }
              
              // Style content area
              const content = modal.querySelector('div:not(:first-child)');
              if (content) {
                content.style.cssText += `
                  background: #ffffff !important;
                  padding: 16px !important;
                  border-radius: 0 0 8px 8px !important;
                  color: #333333 !important;
                `;
              }
            });
          });
        };
        
        // Run styling function periodically
        setTimeout(styleElementModals, 100);
        setTimeout(styleElementModals, 500);
        setTimeout(styleElementModals, 1000);
        const styleInterval = setInterval(styleElementModals, 1000);
        window._builderjs6StyleInterval = styleInterval;
        
        // Also observe for new modals
        const styleObserver = new MutationObserver(() => {
          styleElementModals();
        });
        
        if (mainContainerRef.current) {
          styleObserver.observe(mainContainerRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
          });
        }

        // Function to maintain main container width
        const maintainMainContainerWidth = () => {
          if (mainContainerRef.current) {
            const container = mainContainerRef.current;
            container.style.setProperty('width', '100%', 'important');
            container.style.setProperty('min-width', '100%', 'important');
            container.style.setProperty('max-width', '100%', 'important');
            
            // Also fix iframe width if BuilderJS uses iframe
            const iframes = container.querySelectorAll('iframe');
            iframes.forEach(iframe => {
              iframe.style.setProperty('width', '100%', 'important');
              iframe.style.setProperty('min-width', '100%', 'important');
              iframe.style.setProperty('max-width', '100%', 'important');
            });
          }
        };

        // Set up MutationObserver to maintain main container width
        const widthObserver = new MutationObserver(() => {
          maintainMainContainerWidth();
        });
        
        if (mainContainerRef.current) {
          widthObserver.observe(mainContainerRef.current, {
            attributes: true,
            attributeFilter: ['style', 'class'],
            childList: false,
            subtree: false
          });
        }
        
        // Also set up interval to continuously maintain width
        const widthInterval = setInterval(maintainMainContainerWidth, 100);
        window._builderjs6WidthInterval = widthInterval;
        window._builderjs6WidthObserver = widthObserver;

        // Also fix icons when elements are clicked/selected
        const clickHandler = (e) => {
          // In view mode, prevent all interactions
          if (viewMode) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          setTimeout(fixElementControlIcons, 50);
          setTimeout(fixElementControlIcons, 200);
          
          // Check if an element with builder-element attribute was clicked
          // Use multiple delays to catch the element after BuilderJS processes it
          setTimeout(() => {
            if (!mainContainerRef.current || !builder) return;
            
            // Try multiple ways to find the clicked element
            let clickedElement = e.target.closest('[builder-element]');
            
            // If not found, try parent elements
            if (!clickedElement) {
              let current = e.target;
              for (let i = 0; i < 10 && current; i++) {
                if (current.hasAttribute && current.hasAttribute('builder-element')) {
                  clickedElement = current;
                  break;
                }
                current = current.parentElement;
              }
            }
            
            // Also try finding by checking if element has BuilderJS classes or data attributes
            if (!clickedElement) {
              // Try to find any element that BuilderJS might have selected
              const allBuilderElements = mainContainerRef.current.querySelectorAll('[builder-element]');
              const clickX = e.clientX;
              const clickY = e.clientY;
              
              // Find element at click position
              for (const elem of allBuilderElements) {
                const rect = elem.getBoundingClientRect();
                if (clickX >= rect.left && clickX <= rect.right && 
                    clickY >= rect.top && clickY <= rect.bottom) {
                  clickedElement = elem;
                  break;
                }
              }
            }
            
            // Also check for selected element (BuilderJS might have already selected it)
            if (!clickedElement) {
              clickedElement = mainContainerRef.current.querySelector('[builder-element][selected]') ||
                              mainContainerRef.current.querySelector('[builder-element].selected') ||
                              mainContainerRef.current.querySelector('[builder-element][class*="selected"]');
            }
            
            if (clickedElement) {
              console.log('Found clicked element:', clickedElement);
              // Get element ID
              const elementId = clickedElement.getAttribute('builder-element') || 
                               clickedElement.getAttribute('data-element-id') ||
                               clickedElement.id ||
                               clickedElement.getAttribute('data-id');
              
              console.log('Element ID:', elementId);
              
              if (elementId) {
                // Get element position for floating modal - use getBoundingClientRect for viewport coordinates
                const rect = clickedElement.getBoundingClientRect();
                const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();
                
                // Use viewport coordinates (getBoundingClientRect gives viewport-relative positions)
                let top = rect.top;
                let left = rect.left;
                
                // Position modal above the element, but adjust if it would go off screen
                const modalHeight = 400; // Approximate modal height
                const modalWidth = 400;
                
                // Position above element, or below if not enough space
                if (top < modalHeight + 20) {
                  top = rect.bottom + 10; // Position below element
                } else {
                  top = top - modalHeight - 10; // Position above element
                }
                
                // Keep modal within viewport bounds
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                left = Math.max(10, Math.min(left, viewportWidth - modalWidth - 10));
                top = Math.max(10, Math.min(top, viewportHeight - modalHeight - 10));
                
                // Convert to relative position within canvas wrapper
                if (canvasRect) {
                  top = top - canvasRect.top + (canvasWrapperRef.current?.scrollTop || 0);
                  left = left - canvasRect.left + (canvasWrapperRef.current?.scrollLeft || 0);
                }
                
                // Get element name for display
                const elementName = elementId.replace('Element', '') || 'Element';
                
                console.log('Showing floating modal at:', { top, left, elementId, elementName });
                
                // Show floating modal - DISABLED
                // setFloatingModal({
                //   visible: true,
                //   position: { top, left },
                //   elementId: elementId,
                //   elementName: elementName
                // });
                
                // After modal is rendered, copy element controls to header - DISABLED
                const copyControlsToHeader = () => {
                  // Try multiple selectors to find controls
                  const mainControls = mainContainerRef.current?.querySelector('[class*="element-control"]') ||
                                     mainContainerRef.current?.querySelector('[class*="control-bar"]') ||
                                     mainContainerRef.current?.querySelector('[class*="actions-box"]') ||
                                     mainContainerRef.current?.querySelector('[class*="actions-box-inner"]') ||
                                     mainContainerRef.current?.querySelector('.actions-box') ||
                                     mainContainerRef.current?.querySelector('.actions-box-inner');
                  const floatingActions = document.getElementById('builderjs6-floating-actions');
                  
                  if (mainControls && floatingActions) {
                    // Clone the controls
                    const controlsClone = mainControls.cloneNode(true);
                    // Remove any background styling from cloned controls
                    controlsClone.style.cssText = 'display: flex !important; align-items: center !important; gap: 4px !important; flex-wrap: nowrap !important; background: transparent !important; border: none !important; padding: 0 !important;';
                    
                    // Also style all children to ensure single line
                    const allChildren = controlsClone.querySelectorAll('*');
                    allChildren.forEach(child => {
                      if (child.tagName === 'BUTTON' || child.tagName === 'SPAN' || child.tagName === 'A' || child.tagName === 'DIV') {
                        child.style.cssText += 'background: transparent !important; border: none !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; flex-shrink: 0 !important; white-space: nowrap !important;';
                      }
                    });
                    
                    floatingActions.innerHTML = '';
                    floatingActions.appendChild(controlsClone);
                    
                    // Fix icons immediately and repeatedly
                    if (window._builderjs6FixFloatingIcons) {
                      window._builderjs6FixFloatingIcons();
                      setTimeout(() => window._builderjs6FixFloatingIcons(), 50);
                      setTimeout(() => window._builderjs6FixFloatingIcons(), 150);
                      setTimeout(() => window._builderjs6FixFloatingIcons(), 300);
                      setTimeout(() => window._builderjs6FixFloatingIcons(), 500);
                    }
                  }
                };
                
                // Try multiple times to catch the controls - BuilderJS might render them later
                setTimeout(copyControlsToHeader, 50);
                setTimeout(copyControlsToHeader, 150);
                setTimeout(copyControlsToHeader, 300);
                setTimeout(copyControlsToHeader, 500);
                setTimeout(copyControlsToHeader, 800);
                setTimeout(copyControlsToHeader, 1000);
                
                // Force open settings panel IMMEDIATELY - use multiple methods to ensure it opens
                setIsSettingsOpen(true);
                setActivePanel('settings');
                
                // Force visibility immediately
                setTimeout(() => {
                  const settingsContent = document.querySelector('.builderjs6-settings-content');
                  if (settingsContent) {
                    settingsContent.classList.remove('hidden');
                    settingsContent.classList.add('active');
                    settingsContent.style.display = 'flex';
                    settingsContent.style.visibility = 'visible';
                    settingsContent.style.opacity = '1';
                  }
                  
                  // Ensure settings container is visible
                  const settingsContainer = settingsContainerRef.current;
                  if (settingsContainer) {
                    settingsContainer.style.display = 'block';
                    settingsContainer.style.visibility = 'visible';
                    settingsContainer.style.width = '100%';
                    settingsContainer.style.height = '100%';
                    settingsContainer.style.overflow = 'auto';
                    settingsContainer.style.opacity = '1';
                  }
                  
                  // Call openTab
                  if (builder.settingsTabManager && builder.settingsTabManager.openTab) {
                    builder.settingsTabManager.openTab();
                  }
                }, 0);
                
                // Ensure settingsTabManager is set before selecting
                if (!builder.settingsTabManager) {
                  builder.settingsTabManager = settingsTabManager;
                }
                if (!window.sidebarTabManager) {
                  window.sidebarTabManager = settingsTabManager;
                }
                
                // Select the element - this should trigger renderElementControls
                if (builder.selectElement) {
                  try {
                    builder.selectElement(elementId);
                  } catch (err) {
                    // If selectElement fails, try to render manually
                    setTimeout(() => {
                      if (builder.renderElementControls) {
                        try {
                          builder.renderElementControls(clickedElement);
                        } catch (renderErr) {
                          // Ignore
                        }
                      }
                    }, 100);
                  }
                }
              }
            }
          }, 100); // Delay to let BuilderJS process click first
          
          // Also try after a longer delay as backup
          setTimeout(() => {
            if (!mainContainerRef.current || !builder) return;
            
            // Find selected element
            const selectedElement = mainContainerRef.current.querySelector('[builder-element][selected]') ||
                                   mainContainerRef.current.querySelector('[builder-element].selected') ||
                                   mainContainerRef.current.querySelector('[builder-element][class*="selected"]') ||
                                   mainContainerRef.current.querySelector('[builder-element][data-selected="true"]');
            
            if (selectedElement) {
              // Get element ID and show floating modal
              const elementId = selectedElement.getAttribute('builder-element');
              if (elementId) {
                const rect = selectedElement.getBoundingClientRect();
                const canvasRect = mainContainerRef.current?.getBoundingClientRect();
                
                let top = rect.top;
                let left = rect.left;
                
                if (canvasRect) {
                  top = rect.top - canvasRect.top + mainContainerRef.current.scrollTop;
                  left = rect.left - canvasRect.left + mainContainerRef.current.scrollLeft;
                }
                
                const modalHeight = 400;
                const modalWidth = 400;
                
                if (top < modalHeight + 20) {
                  top = rect.bottom - (canvasRect ? canvasRect.top : 0) + (mainContainerRef.current?.scrollTop || 0) + 10;
                } else {
                  top = top - modalHeight - 10;
                }
                
                left = Math.max(10, Math.min(left, (mainContainerRef.current?.offsetWidth || 1200) - modalWidth - 10));
                
                const elementName = elementId.replace('Element', '') || 'Element';
                
                // Show floating modal - DISABLED
                // setFloatingModal({
                //   visible: true,
                //   position: { top, left },
                //   elementId: elementId,
                //   elementName: elementName
                // });
              }
              
              // Open settings panel
              setIsSettingsOpen(true);
              setActivePanel('settings');
              
              // Ensure settings container is visible
              const settingsContainer = document.getElementById(settingsContainerRef.current.id);
              if (settingsContainer) {
                settingsContainer.style.display = 'block';
                settingsContainer.style.visibility = 'visible';
                settingsContainer.style.width = '100%';
                settingsContainer.style.height = '100%';
              }
              
              // Call openTab
              if (builder.settingsTabManager && builder.settingsTabManager.openTab) {
                builder.settingsTabManager.openTab();
              }
              
              // Try to render controls
              if (builder.renderElementControls) {
                try {
                  const elementId = selectedElement.getAttribute('builder-element');
                  if (elementId) {
                    builder.renderElementControls(elementId);
                  } else {
                    builder.renderElementControls(selectedElement);
                  }
                } catch (renderErr) {
                  // Ignore
                }
              }
            }
          }, 300); // Backup delay
        };
        if (mainContainerRef.current) {
          mainContainerRef.current.addEventListener('click', clickHandler, true);
          window._builderjs6ClickHandler = clickHandler;
        }
        
        // Also listen for mousedown to detect element clicks immediately
        const mouseDownHandler = (e) => {
          if (viewMode) return;
          
          setTimeout(() => {
            if (!mainContainerRef.current || !builder) return;
            
            // Try to find clicked element immediately
            let clickedElement = e.target.closest('[builder-element]');
            
            if (!clickedElement) {
              let current = e.target;
              for (let i = 0; i < 10 && current; i++) {
                if (current.hasAttribute && current.hasAttribute('builder-element')) {
                  clickedElement = current;
                  break;
                }
                current = current.parentElement;
              }
            }
            
            if (clickedElement) {
              const elementId = clickedElement.getAttribute('builder-element');
              if (elementId) {
                // Show modal after a short delay to let BuilderJS process the selection
                setTimeout(() => {
                  const rect = clickedElement.getBoundingClientRect();
                  const canvasRect = mainContainerRef.current?.getBoundingClientRect();
                  
                  let top = rect.top;
                  let left = rect.left;
                  
                  if (canvasRect) {
                    top = rect.top - canvasRect.top + mainContainerRef.current.scrollTop;
                    left = rect.left - canvasRect.left + mainContainerRef.current.scrollLeft;
                  }
                  
                  const modalHeight = 400;
                  const modalWidth = 400;
                  
                  if (top < modalHeight + 20) {
                    top = rect.bottom - (canvasRect ? canvasRect.top : 0) + (mainContainerRef.current?.scrollTop || 0) + 10;
                  } else {
                    top = top - modalHeight - 10;
                  }
                  
                  left = Math.max(10, Math.min(left, (mainContainerRef.current?.offsetWidth || 1200) - modalWidth - 10));
                  
                  const elementName = elementId.replace('Element', '') || 'Element';
                  
                  // setFloatingModal - DISABLED
                  // setFloatingModal({
                  //   visible: true,
                  //   position: { top, left },
                  //   elementId: elementId,
                  //   elementName: elementName
                  // });
                }, 150);
              }
            }
          }, 50);
        };
        
        const mouseUpHandler = () => {
          setTimeout(fixElementControlIcons, 100);
        };
        
        if (mainContainerRef.current) {
          mainContainerRef.current.addEventListener('mousedown', mouseDownHandler, true);
          mainContainerRef.current.addEventListener('mouseup', mouseUpHandler, true);
          window._builderjs6MouseDownHandler = mouseDownHandler;
          window._builderjs6MouseUpHandler = mouseUpHandler;
        }

        // MutationObserver to detect when elements are selected
        const selectionObserver = new MutationObserver((mutations) => {
          if (!mainContainerRef.current || !builder) return;
          
          let shouldCheckSelection = false;
          mutations.forEach(mutation => {
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'selected' || 
                 mutation.attributeName === 'class' ||
                 mutation.attributeName === 'data-selected')) {
              shouldCheckSelection = true;
            }
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              shouldCheckSelection = true;
            }
          });
          
          if (shouldCheckSelection) {
            // Find selected element
            const selectedElement = mainContainerRef.current.querySelector('[builder-element][selected]') ||
                                   mainContainerRef.current.querySelector('[builder-element].selected') ||
                                   mainContainerRef.current.querySelector('[builder-element][class*="selected"]') ||
                                   mainContainerRef.current.querySelector('[builder-element][data-selected="true"]');
            
            if (selectedElement) {
              // Open settings panel
              setIsSettingsOpen(true);
              setActivePanel('settings');
              
              // Ensure settings container is visible
              const settingsContainer = document.getElementById(settingsContainerRef.current.id);
              if (settingsContainer) {
                settingsContainer.style.display = 'block';
                settingsContainer.style.visibility = 'visible';
                settingsContainer.style.width = '100%';
                settingsContainer.style.height = '100%';
                settingsContainer.style.overflow = 'auto';
              }
              
              // Call openTab
              if (builder.settingsTabManager && builder.settingsTabManager.openTab) {
                builder.settingsTabManager.openTab();
              }
              
              // Try to render controls
              if (builder.renderElementControls) {
                setTimeout(() => {
                  try {
                    const elementId = selectedElement.getAttribute('builder-element');
                    if (elementId) {
                      builder.renderElementControls(elementId);
                    } else {
                      builder.renderElementControls(selectedElement);
                    }
                  } catch (renderErr) {
                    // Ignore
                  }
                }, 50);
              }
            }
          }
        });
        
        if (mainContainerRef.current) {
          selectionObserver.observe(mainContainerRef.current, {
            attributes: true,
            attributeFilter: ['selected', 'class', 'data-selected'],
            childList: true,
            subtree: true
          });
        }
        
        window._builderjs6SelectionObserver = selectionObserver;

        // Store observer and function for cleanup
        window._builderjs6IconObserver = iconObserver;
        window._builderjs6FixIcons = fixElementControlIcons;

        // Load initial data or create new page
        let loadingTimeout;
        const onLoadComplete = () => {
          // Clear timeout if loading completes
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
          }
          
          // Set up sidebarTabManager if it exists
          if (builder.settingsTabManager && !window.sidebarTabManager) {
            window.sidebarTabManager = builder.settingsTabManager;
          }
          
          setIsInitialized(true);
          setIsLoading(false);
          
          // Ensure main container width is maintained
          if (mainContainerRef.current) {
            mainContainerRef.current.style.width = '100%';
            mainContainerRef.current.style.minWidth = '100%';
            mainContainerRef.current.style.maxWidth = '100%';
          }
          
          // Fix icons after builder is loaded
          setTimeout(() => {
            if (window._builderjs6FixIcons) {
              window._builderjs6FixIcons();
            }
            // Re-ensure width after icons are fixed
            if (mainContainerRef.current) {
              mainContainerRef.current.style.width = '100%';
              mainContainerRef.current.style.minWidth = '100%';
              mainContainerRef.current.style.maxWidth = '100%';
            }
          }, 500);
        };

        // Timeout fallback to prevent infinite loading (reduced to 5 seconds)
        loadingTimeout = setTimeout(() => {
          setIsInitialized(true);
          setIsLoading(false);
        }, 5000);

        if (initialData) {
          // Ensure theme name matches the theme URL
          let dataToLoad = initialData;
          if (typeof dataToLoad === 'string') {
            try {
              dataToLoad = JSON.parse(dataToLoad);
            } catch (e) {
              dataToLoad = { elementLists: [], formats: {} };
            }
          }
          
          // If data has a theme name, try to get the correct theme name from index.json
          if (dataToLoad.theme && themeUrl) {
            try {
              const themeIndexResponse = await fetch(`${themeUrl}/index.json`);
              if (themeIndexResponse.ok) {
                const themeIndexData = await themeIndexResponse.json();
                // Update theme name to match what BuilderJS expects
                if (themeIndexData.name) {
                  dataToLoad.theme = themeIndexData.name;
                }
              }
            } catch (themeErr) {
              // Continue with original theme name
            }
          }
          
          builder.load(dataToLoad, themeUrl, onLoadComplete);
        } else {
          // Load theme's index.json and store.json to get correct theme name and preview content
          // Use timeout to prevent slow loading
          try {
            const fetchWithTimeout = (url, timeout = 2000) => {
              return Promise.race([
                fetch(url),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Timeout')), timeout)
                )
              ]);
            };
            
            const [themeResponse, storeResponse] = await Promise.all([
              fetchWithTimeout(`${themeUrl}/index.json`, 2000).catch(() => null),
              fetchWithTimeout(`${themeUrl}/store.json`, 2000).catch(() => null)
            ]);
            
            let themeData = null;
            let storeData = null;
            
            if (themeResponse.ok) {
              themeData = await themeResponse.json();
            }
            
            if (storeResponse && storeResponse.ok) {
              storeData = await storeResponse.json();
            }
            
            const themeName = themeData ? (themeData.name || 'Blank') : 'Blank';
            // Find theme name from path
            const themeList = [
              { name: 'Blank', path: '/themes/standard/Y0_Blank' },
              { name: '1 Column Layout', path: '/themes/standard/Y1_a_1_Column_Layout' },
              { name: '1-2 Column Layout', path: '/themes/standard/Y1_b_1-2-Column_Layout' },
              { name: '1-2-1 Column Layout', path: '/themes/standard/Y1_c_1-2-1_Column_Layout_theme' },
              { name: '1-3 Column Layout', path: '/themes/standard/Y1_d_1-3_Column_Layout_theme' },
              { name: '1-3-1 Column Layout', path: '/themes/standard/Y1_e_1-3-1_Column_Layout_theme' },
              { name: '1-3-2 Column Layout', path: '/themes/standard/Y1_f_1-3-2_Column_Layout_theme' },
              { name: '2 Column Layout', path: '/themes/standard/Y2_a_2_Column_Layout_theme' },
              { name: '2-1 Column Layout', path: '/themes/standard/Y2_b_2-1_Column_Layout_theme' },
              { name: '2-1-2 Column Layout', path: '/themes/standard/Y2_c_2-1-2_Column_Layout_theme' },
              { name: '3 Column Layout', path: '/themes/standard/Y3_a_3_Column_Layout_theme' },
              { name: '3-1-3 Column Layout', path: '/themes/standard/Y3_b_3-1-3_Column_Layout_theme' },
              { name: 'Pricing Table', path: '/themes/standard/Y4_Pricing_Table_theme' },
              { name: 'Listing Tables', path: '/themes/standard/Y4_Listing_Tables_theme' },
              { name: 'Forms Building', path: '/themes/standard/Y4_Forms_Building_theme' },
            ];
            const displayName = themeData ? (themeData.title || themeList.find(t => t.path === themeUrl)?.name) : (themeList.find(t => t.path === themeUrl)?.name || 'Blank');
            
            // Set theme name to display
            setCurrentThemeName(displayName);
            
            // Use store.json data if available (theme preview), otherwise use empty data
            const themeDataToLoad = storeData && storeData.elementLists ? storeData : {
              theme: themeName,
              elementLists: [],
              formats: {}
            };
            
            builder.load(themeDataToLoad, themeUrl, onLoadComplete);
          } catch (error) {
            // Fallback if fetch fails
            const emptyData = {
              theme: 'Blank',
              elementLists: [],
              formats: {}
            };
            builder.load(emptyData, themeUrl, onLoadComplete);
          }
        }

        // Event listeners (backup in case callback doesn't fire)
        if (builder.addEventListener) {
          builder.addEventListener('loaded', () => {
            if (loadingTimeout) {
              clearTimeout(loadingTimeout);
            }
            setIsInitialized(true);
            setIsLoading(false);
          });

          builder.addEventListener('changed', () => {
            // Handle changes
            setTimeout(fixElementControlIcons, 50);
          });
          
          // Listen for element selection events - try multiple event names
          const handleElementSelected = (event) => {
            console.log('BuilderJS element selected event:', event);
            // Find the selected element
            setTimeout(() => {
              if (!mainContainerRef.current || !builder) return;
              
              const selectedElement = mainContainerRef.current.querySelector('[builder-element][selected]') ||
                                     mainContainerRef.current.querySelector('[builder-element].selected') ||
                                     mainContainerRef.current.querySelector('[builder-element][class*="selected"]') ||
                                     mainContainerRef.current.querySelector('[builder-element][data-selected="true"]');
              
              console.log('Selected element found:', selectedElement);
              
              if (selectedElement) {
                const elementId = selectedElement.getAttribute('builder-element');
                console.log('Selected element ID:', elementId);
                
                if (elementId) {
                  // Get element position for floating modal - use getBoundingClientRect for viewport coordinates
                  const rect = selectedElement.getBoundingClientRect();
                  const canvasRect = canvasWrapperRef.current?.getBoundingClientRect();
                  
                  // Use viewport coordinates
                  let top = rect.top;
                  let left = rect.left;
                  
                  const modalHeight = 400;
                  const modalWidth = 400;
                  
                  // Position above element, or below if not enough space
                  if (top < modalHeight + 20) {
                    top = rect.bottom + 10; // Position below element
                  } else {
                    top = top - modalHeight - 10; // Position above element
                  }
                  
                  // Keep modal within viewport bounds
                  const viewportWidth = window.innerWidth;
                  const viewportHeight = window.innerHeight;
                  left = Math.max(10, Math.min(left, viewportWidth - modalWidth - 10));
                  top = Math.max(10, Math.min(top, viewportHeight - modalHeight - 10));
                  
                  // Convert to relative position within canvas wrapper
                  if (canvasRect) {
                    top = top - canvasRect.top + (canvasWrapperRef.current?.scrollTop || 0);
                    left = left - canvasRect.left + (canvasWrapperRef.current?.scrollLeft || 0);
                  }
                  
                  const elementName = elementId.replace('Element', '') || 'Element';
                  
                  console.log('Showing floating modal from event at:', { top, left, elementId, elementName });
                  
                  // Show floating modal - DISABLED
                  // setFloatingModal({
                  //   visible: true,
                  //   position: { top, left },
                  //   elementId: elementId,
                  //   elementName: elementName
                  // });
                  
                  // Copy controls to header after modal renders - DISABLED
                  setTimeout(() => {
                    const mainControls = mainContainerRef.current?.querySelector('[class*="element-control"]') ||
                                       mainContainerRef.current?.querySelector('[class*="control-bar"]') ||
                                       mainContainerRef.current?.querySelector('[class*="actions-box"]') ||
                                       mainContainerRef.current?.querySelector('[class*="actions-box-inner"]');
                    const floatingActions = document.getElementById('builderjs6-floating-actions');
                    
                    if (mainControls && floatingActions) {
                      const controlsClone = mainControls.cloneNode(true);
                      controlsClone.style.cssText = 'display: flex !important; align-items: center !important; gap: 4px !important; flex-wrap: nowrap !important; background: transparent !important; border: none !important; padding: 0 !important;';
                      
                      const allChildren = controlsClone.querySelectorAll('*');
                      allChildren.forEach(child => {
                        if (child.tagName === 'BUTTON' || child.tagName === 'SPAN' || child.tagName === 'A' || child.tagName === 'DIV') {
                          child.style.cssText += 'background: transparent !important; border: none !important; display: inline-flex !important; align-items: center !important; justify-content: center !important; flex-shrink: 0 !important; white-space: nowrap !important;';
                        }
                      });
                      
                      floatingActions.innerHTML = '';
                      floatingActions.appendChild(controlsClone);
                      
                      if (window._builderjs6FixFloatingIcons) {
                        window._builderjs6FixFloatingIcons();
                        setTimeout(() => window._builderjs6FixFloatingIcons(), 50);
                        setTimeout(() => window._builderjs6FixFloatingIcons(), 150);
                      }
                    }
                  }, 100);
                }
              }
              
              setIsSettingsOpen(true);
              setActivePanel('settings');
              if (builder.settingsTabManager && builder.settingsTabManager.openTab) {
                setTimeout(() => {
                  builder.settingsTabManager.openTab();
                }, 100);
              }
            }, 50);
          };
          
          builder.addEventListener('selected', handleElementSelected);
          builder.addEventListener('elementSelected', handleElementSelected);
          builder.addEventListener('select', handleElementSelected);
          
          builder.addEventListener('unselected', () => {
            setFloatingModal({ ...floatingModal, visible: false });
            setIsSettingsOpen(false);
            setActivePanel('widgets');
          });
        }
        
        // Use MutationObserver to detect when elements get selected by BuilderJS
        if (mainContainerRef.current) {
          const selectionObserver = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
              if (mutation.type === 'attributes' && 
                  (mutation.attributeName === 'class' || mutation.attributeName === 'selected' || mutation.attributeName === 'data-selected')) {
                // Check if any element is now selected
                const selectedElement = mainContainerRef.current.querySelector('[builder-element][selected]') ||
                                       mainContainerRef.current.querySelector('[builder-element].selected') ||
                                       mainContainerRef.current.querySelector('[builder-element][class*="selected"]') ||
                                       mainContainerRef.current.querySelector('[builder-element][data-selected="true"]');
                
                if (selectedElement) {
                  setIsSettingsOpen(true);
                  setActivePanel('settings');
                  if (builder.settingsTabManager && builder.settingsTabManager.openTab) {
                    setTimeout(() => {
                      builder.settingsTabManager.openTab();
                    }, 100);
                  }
                } else {
                  // No element selected, close settings
                  const hasSelected = mainContainerRef.current.querySelector('[selected], .selected, [class*="selected"], [data-selected="true"]');
                  if (!hasSelected) {
                    setIsSettingsOpen(false);
                    setActivePanel('widgets');
                  }
                }
              }
            });
          });
          
          if (mainContainerRef.current) {
            selectionObserver.observe(mainContainerRef.current, {
              attributes: true,
              attributeFilter: ['class', 'selected', 'data-selected'],
              subtree: true
            });
            window._builderjs6SelectionObserver = selectionObserver;
          }
        }

      } catch (error) {
        const errorMsg = error.message || 'Failed to initialize BuilderJS 6.0';
        // Only show error if it's not a container issue (already handled above)
        if (!errorMsg.includes('container') && !errorMsg.includes('Container')) {
          toast.error(errorMsg);
        }
        setIsLoading(false);
      }
    };

    initializeBuilder();

    return () => {
      mounted = false;
      if (window.builder) {
        delete window.builder;
      }
      if (window.sidebarTabManager) {
        delete window.sidebarTabManager;
      }
      if (window._builderjs6IconObserver) {
        window._builderjs6IconObserver.disconnect();
        delete window._builderjs6IconObserver;
      }
      if (window._builderjs6IconInterval) {
        clearInterval(window._builderjs6IconInterval);
        delete window._builderjs6IconInterval;
      }
      if (window._builderjs6FloatingIconInterval) {
        clearInterval(window._builderjs6FloatingIconInterval);
        delete window._builderjs6FloatingIconInterval;
      }
      if (window._builderjs6WidthInterval) {
        clearInterval(window._builderjs6WidthInterval);
        delete window._builderjs6WidthInterval;
      }
      if (window._builderjs6WidthObserver) {
        window._builderjs6WidthObserver.disconnect();
        delete window._builderjs6WidthObserver;
      }
      if (window._builderjs6SelectionObserver) {
        window._builderjs6SelectionObserver.disconnect();
        delete window._builderjs6SelectionObserver;
      }
      if (window._builderjs6ClickHandler && mainContainerRef.current) {
        mainContainerRef.current.removeEventListener('click', window._builderjs6ClickHandler, true);
        delete window._builderjs6ClickHandler;
      }
      if (window._builderjs6MouseDownHandler && mainContainerRef.current) {
        mainContainerRef.current.removeEventListener('mousedown', window._builderjs6MouseDownHandler, true);
        delete window._builderjs6MouseDownHandler;
      }
      if (window._builderjs6MouseUpHandler && mainContainerRef.current) {
        mainContainerRef.current.removeEventListener('mouseup', window._builderjs6MouseUpHandler, true);
        delete window._builderjs6MouseUpHandler;
      }
      if (window._builderjs6FixIcons) {
        delete window._builderjs6FixIcons;
      }
      if (window._builderjs6SelectionObserver) {
        window._builderjs6SelectionObserver.disconnect();
        delete window._builderjs6SelectionObserver;
      }
      window.removeEventListener('error', errorHandler, true);
      // Restore original alert
      if (window._builderjs6OriginalAlert) {
        window.alert = window._builderjs6OriginalAlert;
        delete window._builderjs6OriginalAlert;
      }
      // Restore original scrollIntoView
      if (Element.prototype.scrollIntoView && Element.prototype.scrollIntoView._original) {
        Element.prototype.scrollIntoView = Element.prototype.scrollIntoView._original;
        delete Element.prototype.scrollIntoView._original;
      }
    };
  }, [initialData, themeUrl]);

  const handleSave = () => {
    if (!builderInstanceRef.current) {
      toast.error('Builder not initialized');
      return;
    }

    try {
      // Check available methods
      const builder = builderInstanceRef.current;
      
      // Try getData() first (most common BuilderJS method)
      let jsonData;
      if (typeof builder.getData === 'function') {
        jsonData = builder.getData();
      } else if (typeof builder.getJson === 'function') {
        jsonData = builder.getJson();
      } else {
        toast.error('BuilderJS instance does not have getData() or getJson() method');
        return;
      }
      
      // Get HTML data
      let htmlData = '';
      if (typeof builder.getHtml === 'function') {
        htmlData = builder.getHtml();
      }
      
      if (!jsonData) {
        toast.error('No template data to save');
        return;
      }

      // If onSave callback is provided, show modal and let parent handle it
      if (onSave) {
        setPendingSaveData({ jsonData, htmlData });
        setTemplateName(`BuilderJS6 Template ${new Date().toLocaleString()}`);
        setShowSaveModal(true);
        return;
      }

      // Otherwise, show modal to get template name
      setPendingSaveData({ jsonData, htmlData });
      setTemplateName(`BuilderJS6 Template ${new Date().toLocaleString()}`);
      setShowSaveModal(true);
    } catch (error) {
      toast.error('Failed to prepare template data');
    }
  };

  const handleConfirmSave = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    if (!pendingSaveData) {
      toast.error('No template data to save');
      return;
    }

    setShowSaveModal(false);
    const { jsonData, htmlData } = pendingSaveData;

    try {
      // If onSave callback is provided, use it (for parent component handling)
      if (onSave) {
        onSave(jsonData, htmlData, templateName.trim());
        setPendingSaveData(null);
        setTemplateName('');
        return;
      }

      // Otherwise, make API call directly
      // Get userId
      const userId = config.getUserId();
      if (!userId) {
        toast.error('User not authenticated. Please login again.');
        return;
      }

      // Parse JSON if it's a string
      let jsonObject = jsonData;
      if (typeof jsonData === 'string') {
        try {
          jsonObject = JSON.parse(jsonData);
        } catch (e) {
          jsonObject = jsonData;
        }
      }

      // Validate jsonObject
      if (typeof jsonObject !== 'object' || jsonObject === null) {
        toast.error('Invalid template data. Please try again.');
        return;
      }

      // Prepare request data
      const requestData = {
        userId,
        templateName: templateName.trim(),
        html: htmlData || '',
        json: jsonObject,
      };

      // Get template ID from URL if editing existing template
      const urlPath = window.location.pathname;
      const pathParts = urlPath.split('/');
      const templateId = pathParts[pathParts.length - 1];
      const isNewTemplate = !templateId || templateId === 'builderjs6' || templateId === 'new';

      let response;
      if (!isNewTemplate && templateId) {
        // Update existing template
        response = await AxiosInstance.put(`/templates/${templateId}`, requestData);
      } else {
        // Create new template
        response = await AxiosInstance.post('/templates', requestData);
      }

      if (response.data?.statusCode === 200 || response.data?.statusCode === 201) {
        toast.success('Template saved successfully');
        
        // If this was a new template, navigate to the edit URL
        if (isNewTemplate && response.data?.data?.TemplateId) {
          window.history.pushState({}, '', `/builderjs6/${response.data.data.TemplateId}`);
        }
      } else {
        throw new Error(response.data?.message || 'Failed to save template');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save template';
      toast.error(errorMessage);
    } finally {
      setPendingSaveData(null);
      setTemplateName('');
    }
  };

  const handleCancelSave = () => {
    setShowSaveModal(false);
    setPendingSaveData(null);
    setTemplateName('');
  };

  const handleExport = () => {
    if (!builderInstanceRef.current) {
      toast.error('Builder not initialized');
      return;
    }

    try {
      const html = builderInstanceRef.current.getHtml();
      if (onExport) {
        onExport(html);
      } else {
        // Download HTML
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'exported-page.html';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Page exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export page');
    }
  };

  const handleClear = async () => {
    if (!builderInstanceRef.current) {
      toast.error('Builder not initialized');
      return;
    }

    if (window.confirm('Are you sure you want to clear the page? This action cannot be undone.')) {
      try {
        // Keep current theme URL - just clear all elements
        const currentTheme = currentThemeUrl || themeUrl;
        
        // Fetch the correct theme name from index.json (BuilderJS expects internal theme name, not display name)
        let themeName = 'Blank';
        try {
          const themeIndexResponse = await fetch(`${currentTheme}/index.json`);
          if (themeIndexResponse.ok) {
            const themeIndexData = await themeIndexResponse.json();
            themeName = themeIndexData.name || 'Blank';
          }
        } catch (themeErr) {
          themeName = 'Blank';
        }
        
        // Create completely empty data structure with correct theme name
        const emptyData = {
          theme: themeName, // Use internal theme name from index.json
          elementLists: [], // Clear all elements
          formats: {} // Clear all formats
        };
        
        // Load empty data with current theme (don't change theme, just clear content)
        builderInstanceRef.current.load(emptyData, currentTheme, () => {
          toast.success('Page cleared successfully');
        });
      } catch (error) {
        toast.error('Failed to clear page');
      }
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the page?')) {
      if (initialData) {
        builderInstanceRef.current?.load(initialData);
        toast.success('Page reset');
      }
    }
  };

  // Available themes
  const themes = [
    { name: 'Blank', path: '/themes/standard/Y0_Blank' },
    { name: '1 Column Layout', path: '/themes/standard/Y1_a_1_Column_Layout' },
    { name: '1-2 Column Layout', path: '/themes/standard/Y1_b_1-2-Column_Layout' },
    { name: '1-2-1 Column Layout', path: '/themes/standard/Y1_c_1-2-1_Column_Layout_theme' },
    { name: '1-3 Column Layout', path: '/themes/standard/Y1_d_1-3_Column_Layout_theme' },
    { name: '1-3-1 Column Layout', path: '/themes/standard/Y1_e_1-3-1_Column_Layout_theme' },
    { name: '1-3-2 Column Layout', path: '/themes/standard/Y1_f_1-3-2_Column_Layout_theme' },
    { name: '2 Column Layout', path: '/themes/standard/Y2_a_2_Column_Layout_theme' },
    { name: '2-1 Column Layout', path: '/themes/standard/Y2_b_2-1_Column_Layout_theme' },
    { name: '2-1-2 Column Layout', path: '/themes/standard/Y2_c_2-1-2_Column_Layout_theme' },
    { name: '3 Column Layout', path: '/themes/standard/Y3_a_3_Column_Layout_theme' },
    { name: '3-1-3 Column Layout', path: '/themes/standard/Y3_b_3-1-3_Column_Layout_theme' },
    { name: 'Pricing Table', path: '/themes/standard/Y4_Pricing_Table_theme' },
    { name: 'Listing Tables', path: '/themes/standard/Y4_Listing_Tables_theme' },
    { name: 'Forms Building', path: '/themes/standard/Y4_Forms_Building_theme' },
  ];

  const handleChangeTheme = (newThemePath) => {
    if (!builderInstanceRef.current) {
      toast.error('Builder not initialized');
      return;
    }

    if (window.confirm('Changing theme will clear current content. Continue?')) {
      try {
        setIsLoading(true);
        setCurrentThemeUrl(newThemePath);
        setShowThemeSelector(false);

        // Get theme name from index.json and load default content from store.json
        Promise.all([
          fetch(`${newThemePath}/index.json`).then(r => r.json()),
          fetch(`${newThemePath}/store.json`).then(r => r.json()).catch(() => null)
        ])
          .then(([themeData, storeData]) => {
            const themeName = themeData.name || 'Blank';
            const displayName = themeData.title || themes.find(t => t.path === newThemePath)?.name || themeName;
            
            // Update theme name to display on left
            setCurrentThemeName(displayName);
            
            // Use store.json data if available (theme preview), otherwise use empty data
            const themeDataToLoad = storeData && storeData.elementLists ? storeData : {
              theme: themeName,
              elementLists: [],
              formats: {}
            };
            
            builderInstanceRef.current.load(themeDataToLoad, newThemePath, () => {
              // Ensure main container maintains width after theme change - IMMEDIATELY with !important
              if (mainContainerRef.current) {
                const container = mainContainerRef.current;
                container.style.setProperty('width', '100%', 'important');
                container.style.setProperty('min-width', '100%', 'important');
                container.style.setProperty('max-width', '100%', 'important');
                
                // Also fix iframe width if BuilderJS uses iframe
                const iframes = container.querySelectorAll('iframe');
                iframes.forEach(iframe => {
                  iframe.style.setProperty('width', '100%', 'important');
                  iframe.style.setProperty('min-width', '100%', 'important');
                  iframe.style.setProperty('max-width', '100%', 'important');
                });
              }
              
              // Refresh widgets box to show theme-specific widgets
              setTimeout(() => {
                if (builderInstanceRef.current && builderInstanceRef.current.widgetsBox) {
                  try {
                    // Re-render widgets for the new theme
                    if (typeof builderInstanceRef.current.widgetsBox.render === 'function') {
                      builderInstanceRef.current.widgetsBox.render();
                    }
                  } catch (widgetErr) {
                    // Ignore widget render errors
                  }
                }
                
                // Ensure widgets container is visible
                if (widgetsContainerRef.current) {
                  widgetsContainerRef.current.style.display = 'block';
                  widgetsContainerRef.current.style.visibility = 'visible';
                }
                
                // Re-ensure main container width after a delay - with !important
                if (mainContainerRef.current) {
                  const container = mainContainerRef.current;
                  container.style.setProperty('width', '100%', 'important');
                  container.style.setProperty('min-width', '100%', 'important');
                  container.style.setProperty('max-width', '100%', 'important');
                }
                
                // Additional delays to ensure width is maintained
                setTimeout(() => {
                  if (mainContainerRef.current) {
                    const container = mainContainerRef.current;
                    container.style.setProperty('width', '100%', 'important');
                    container.style.setProperty('min-width', '100%', 'important');
                    container.style.setProperty('max-width', '100%', 'important');
                  }
                }, 500);
                setTimeout(() => {
                  if (mainContainerRef.current) {
                    const container = mainContainerRef.current;
                    container.style.setProperty('width', '100%', 'important');
                    container.style.setProperty('min-width', '100%', 'important');
                    container.style.setProperty('max-width', '100%', 'important');
                  }
                }, 1000);
              }, 300);
              
              setIsLoading(false);
              toast.success(`Theme changed to ${displayName}${storeData ? ' (with preview content)' : ''}`);
            });
          })
          .catch(error => {
            // Fallback: try to load store.json directly
            fetch(`${newThemePath}/store.json`)
              .then(response => response.json())
              .then(storeData => {
                // Find theme name from themes array
                const themeObj = themes.find(t => t.path === newThemePath);
                const displayName = themeObj ? themeObj.name : 'Blank';
                setCurrentThemeName(displayName);
                
                // Use store.json data if available
                const themeDataToLoad = storeData && storeData.elementLists ? storeData : {
                  theme: 'Blank',
                  elementLists: [],
                  formats: {}
                };
                
                builderInstanceRef.current.load(themeDataToLoad, newThemePath, () => {
                  // Ensure main container maintains width after theme change
                  if (mainContainerRef.current) {
                    mainContainerRef.current.style.width = '100%';
                    mainContainerRef.current.style.minWidth = '100%';
                    mainContainerRef.current.style.maxWidth = '100%';
                  }
                  
                  // Refresh widgets box
                  setTimeout(() => {
                    if (builderInstanceRef.current && builderInstanceRef.current.widgetsBox) {
                      try {
                        if (typeof builderInstanceRef.current.widgetsBox.render === 'function') {
                          builderInstanceRef.current.widgetsBox.render();
                        }
                      } catch (widgetErr) {
                        // Ignore errors
                      }
                    }
                    if (widgetsContainerRef.current) {
                      widgetsContainerRef.current.style.display = 'block';
                      widgetsContainerRef.current.style.visibility = 'visible';
                    }
                    
                    // Re-ensure main container width after a delay
                    if (mainContainerRef.current) {
                      mainContainerRef.current.style.width = '100%';
                      mainContainerRef.current.style.minWidth = '100%';
                      mainContainerRef.current.style.maxWidth = '100%';
                    }
                  }, 300);
                  
                  setIsLoading(false);
                  toast.success(`Theme changed to ${displayName}${storeData ? ' (with preview)' : ''}`);
                });
              })
              .catch(() => {
                // Final fallback: empty data
                const themeObj = themes.find(t => t.path === newThemePath);
                const displayName = themeObj ? themeObj.name : 'Blank';
                setCurrentThemeName(displayName);
                
                const emptyData = {
                  theme: 'Blank',
                  elementLists: [],
                  formats: {}
                };
                builderInstanceRef.current.load(emptyData, newThemePath, () => {
                  // Ensure main container maintains width after theme change
                  if (mainContainerRef.current) {
                    mainContainerRef.current.style.width = '100%';
                    mainContainerRef.current.style.minWidth = '100%';
                    mainContainerRef.current.style.maxWidth = '100%';
                  }
                  
                  setTimeout(() => {
                    if (builderInstanceRef.current && builderInstanceRef.current.widgetsBox) {
                      try {
                        if (typeof builderInstanceRef.current.widgetsBox.render === 'function') {
                          builderInstanceRef.current.widgetsBox.render();
                        }
                      } catch (widgetErr) {
                        // Ignore errors
                      }
                    }
                    if (widgetsContainerRef.current) {
                      widgetsContainerRef.current.style.display = 'block';
                      widgetsContainerRef.current.style.visibility = 'visible';
                    }
                    
                    // Re-ensure main container width after a delay
                    if (mainContainerRef.current) {
                      mainContainerRef.current.style.width = '100%';
                      mainContainerRef.current.style.minWidth = '100%';
                      mainContainerRef.current.style.maxWidth = '100%';
                    }
                  }, 300);
                  
                  setIsLoading(false);
                  toast.success(`Theme changed to ${displayName}`);
                });
              });
          });
      } catch (error) {
        toast.error('Failed to change theme');
        setIsLoading(false);
      }
    }
  };

  const handleThemeSelectorToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowThemeSelector(prev => !prev);
  };

  const handleDeviceView = (device) => {
    setDeviceView(device);
    
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      const mainContainer = mainContainerRef.current;
      const canvasWrapper = canvasWrapperRef.current || mainContainer?.parentElement;
      
      if (mainContainer) {
        // Update canvas wrapper alignment and display
        if (canvasWrapper) {
          if (device === 'desktop') {
            canvasWrapper.style.setProperty('display', 'flex', 'important');
            canvasWrapper.style.setProperty('align-items', 'stretch', 'important');
            canvasWrapper.style.setProperty('justify-content', 'flex-start', 'important');
          } else {
            canvasWrapper.style.setProperty('display', 'flex', 'important');
            canvasWrapper.style.setProperty('align-items', 'center', 'important');
            canvasWrapper.style.setProperty('justify-content', 'center', 'important');
          }
        }
        
        // Remove all existing width/margin styles first
        mainContainer.style.removeProperty('width');
        mainContainer.style.removeProperty('min-width');
        mainContainer.style.removeProperty('max-width');
        mainContainer.style.removeProperty('margin');
        
        // Set width based on device view
        if (device === 'desktop') {
          mainContainer.style.width = '100%';
          mainContainer.style.minWidth = '100%';
          mainContainer.style.maxWidth = '100%';
          mainContainer.style.margin = '0';
        } else if (device === 'tablet') {
          mainContainer.style.width = '768px';
          mainContainer.style.minWidth = '768px';
          mainContainer.style.maxWidth = '768px';
          mainContainer.style.margin = '0 auto';
        } else if (device === 'mobile') {
          mainContainer.style.width = '375px';
          mainContainer.style.minWidth = '375px';
          mainContainer.style.maxWidth = '375px';
          mainContainer.style.margin = '0 auto';
        }
        
        // Force style recalculation
        mainContainer.style.display = 'block';
        void mainContainer.offsetWidth;
        mainContainer.style.display = '';
        
        // Update iframes with a slight delay
        setTimeout(() => {
          const iframes = mainContainer.querySelectorAll('iframe');
          iframes.forEach(iframe => {
            if (device === 'desktop') {
              iframe.style.width = '100%';
              iframe.style.minWidth = '100%';
              iframe.style.maxWidth = '100%';
            } else if (device === 'tablet') {
              iframe.style.width = '768px';
              iframe.style.minWidth = '768px';
              iframe.style.maxWidth = '768px';
            } else if (device === 'mobile') {
              iframe.style.width = '375px';
              iframe.style.minWidth = '375px';
              iframe.style.maxWidth = '375px';
            }
          });
        }, 50);
      }
    }, 0);
  };

  // Apply device view styles when deviceView changes
  useEffect(() => {
    const mainContainer = mainContainerRef.current;
    const canvasWrapper = mainContainer?.parentElement;
    
    if (mainContainer && isInitialized) {
      // Update canvas wrapper to center content for tablet/mobile
      if (canvasWrapper && canvasWrapper.classList.contains('builderjs6-canvas-wrapper')) {
        if (deviceView === 'desktop') {
          canvasWrapper.style.setProperty('align-items', 'stretch', 'important');
          canvasWrapper.style.setProperty('justify-content', 'flex-start', 'important');
        } else {
          canvasWrapper.style.setProperty('align-items', 'center', 'important');
          canvasWrapper.style.setProperty('justify-content', 'center', 'important');
        }
      }
      
      // Set width based on device view
      if (deviceView === 'desktop') {
        mainContainer.style.setProperty('width', '100%', 'important');
        mainContainer.style.setProperty('min-width', '100%', 'important');
        mainContainer.style.setProperty('max-width', '100%', 'important');
        mainContainer.style.setProperty('margin', '0', 'important');
      } else if (deviceView === 'tablet') {
        mainContainer.style.setProperty('width', '768px', 'important');
        mainContainer.style.setProperty('min-width', '768px', 'important');
        mainContainer.style.setProperty('max-width', '768px', 'important');
        mainContainer.style.setProperty('margin', '0 auto', 'important');
      } else if (deviceView === 'mobile') {
        mainContainer.style.setProperty('width', '375px', 'important');
        mainContainer.style.setProperty('min-width', '375px', 'important');
        mainContainer.style.setProperty('max-width', '375px', 'important');
        mainContainer.style.setProperty('margin', '0 auto', 'important');
      }
      
      // Also update iframes inside the container
      const iframes = mainContainer.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        if (deviceView === 'desktop') {
          iframe.style.setProperty('width', '100%', 'important');
          iframe.style.setProperty('min-width', '100%', 'important');
          iframe.style.setProperty('max-width', '100%', 'important');
        } else if (deviceView === 'tablet') {
          iframe.style.setProperty('width', '768px', 'important');
          iframe.style.setProperty('min-width', '768px', 'important');
          iframe.style.setProperty('max-width', '768px', 'important');
        } else if (deviceView === 'mobile') {
          iframe.style.setProperty('width', '375px', 'important');
          iframe.style.setProperty('min-width', '375px', 'important');
          iframe.style.setProperty('max-width', '375px', 'important');
        }
      });
      
      // Force reflow to ensure styles are applied
      void mainContainer.offsetHeight;
    }
  }, [deviceView, isInitialized]);

  return (
    <div className="builderjs6-wrapper">
      {isLoading && (
        <div className="builderjs6-loading-overlay">
          <div className="builderjs6-loading-spinner"></div>
          <p>Loading BuilderJS 6.0...</p>
        </div>
      )}
      {/* View Mode Banner */}
      {viewMode && (
        <div style={{
          background: '#3b82f6',
          color: 'white',
          padding: '12px 24px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span className="material-icons" style={{ fontSize: '20px' }}>visibility</span>
          <span>View Mode - Template is read-only</span>
        </div>
      )}
      {/* Top Header Bar */}
      <div className="builderjs6-header">
        <div className="builderjs6-header-left">
          <button className="builderjs6-layout-btn">
            <span className="material-icons">palette</span>
            {currentThemeName}
          </button>
          {!viewMode && (
            <div className="builderjs6-theme-selector-wrapper">
              <button className="builderjs6-theme-btn" onClick={handleThemeSelectorToggle}>
                <span className="material-icons">palette</span>
                Change Theme
              </button>
            {showThemeSelector && (
              <div className="builderjs6-theme-dropdown">
                <div className="builderjs6-theme-dropdown-header">
                  <span>Select Theme</span>
                  <button onClick={handleThemeSelectorToggle} className="builderjs6-theme-close">
                    <span className="material-icons">close</span>
                  </button>
                </div>
                <div className="builderjs6-theme-list">
                  {themes && themes.length > 0 ? (
                    themes.map((theme, index) => (
                      <button
                        key={index}
                        className={`builderjs6-theme-item ${currentThemeUrl === theme.path ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleChangeTheme(theme.path);
                        }}
                      >
                        <span className="builderjs6-theme-name">{theme.name}</span>
                        {currentThemeUrl === theme.path && (
                          <span className="material-icons builderjs6-theme-check">check</span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div style={{ padding: '12px', color: '#666', textAlign: 'center' }}>
                      No themes available
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          )}
        </div>

        <div className="builderjs6-header-center">
          <div className="builderjs6-device-preview">
            <button 
              className={`builderjs6-device-btn ${deviceView === 'desktop' ? 'active' : ''}`}
              onClick={() => handleDeviceView('desktop')}
              title="Desktop"
            >
              <span className="material-icons">computer</span>
            </button>
            <button 
              className={`builderjs6-device-btn ${deviceView === 'tablet' ? 'active' : ''}`}
              onClick={() => handleDeviceView('tablet')}
              title="Tablet"
            >
              <span className="material-icons">tablet</span>
            </button>
            <button 
              className={`builderjs6-device-btn ${deviceView === 'mobile' ? 'active' : ''}`}
              onClick={() => handleDeviceView('mobile')}
              title="Mobile"
            >
              <span className="material-icons">phone_android</span>
            </button>
          </div>
        </div>

        <div className="builderjs6-header-right">
          {!viewMode && (
            <>
              <button className="builderjs6-action-btn clear" onClick={handleClear}>
                Clear
              </button>
              <button className="builderjs6-action-btn reset" onClick={handleReset}>
                <span className="material-icons">refresh</span>
                Reset
              </button>
              <button className="builderjs6-action-btn save" onClick={handleSave}>
                <span className="material-icons">save</span>
                Save
              </button>
            </>
          )}
          <button className="builderjs6-action-btn download">
            <span className="material-icons">download</span>
            Download bundle
          </button>
          <button className="builderjs6-action-btn export" onClick={handleExport}>
            <span className="material-icons">file_download</span>
            Export
            <span className="material-icons">arrow_drop_down</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="builderjs6-content">
        {/* Main Canvas */}
        <div 
          ref={canvasWrapperRef}
          className={`builderjs6-canvas-wrapper ${viewMode ? 'view-mode' : ''}`} 
          style={viewMode ? { width: '100%', position: 'relative' } : { position: 'relative' }}
        >
          <div 
            ref={mainContainerRef}
            id="builderjs6-main"
            className={`builderjs6-main-container builderjs6-${deviceView}-view ${viewMode ? 'view-mode-readonly' : ''}`}
            style={viewMode ? { pointerEvents: 'none', userSelect: 'none' } : {}}
          ></div>
        </div>

        {/* Right Sidebar - Fixed Tabs Header */}
        {!viewMode && (
        <div className="builderjs6-sidebar">
          <div className="builderjs6-widgets-header">
            <div className="builderjs6-widgets-tabs">
              <button 
                className={`builderjs6-tab-btn ${activePanel === 'widgets' ? 'active' : ''}`}
                onClick={() => {
                  setActivePanel('widgets');
                  setIsSettingsOpen(false);
                }}
                title="Widgets"
              >
                <span className="material-icons">grid_view</span>
              </button>
              <button 
                className={`builderjs6-tab-btn ${activePanel === 'settings' ? 'active' : ''}`}
                onClick={() => {
                  setActivePanel('settings');
                  setIsSettingsOpen(true);
                }}
                title="Settings"
              >
                <span className="material-icons">settings</span>
              </button>
              <button className="builderjs6-tab-btn">
                <span className="material-icons">palette</span>
              </button>
              <button className="builderjs6-tab-btn">
                <span className="material-icons">menu</span>
              </button>
            </div>
          </div>

          {/* Widgets Panel Content */}
          <div className={`builderjs6-widgets-content ${activePanel === 'widgets' ? 'active' : 'hidden'}`}>
            <div className="builderjs6-widgets-hint">
              <span className="material-icons">auto_awesome</span>
              Just drag & drop the widget into the page content to insert!
            </div>

            <div 
              ref={widgetsContainerRef}
              id="builderjs6-widgets"
              className="builderjs6-widgets-container"
            ></div>
          </div>

          {/* Settings Panel Content */}
          <div 
            className={`builderjs6-settings-content ${activePanel === 'settings' ? 'active' : 'hidden'}`}
            style={{
              display: activePanel === 'settings' ? 'flex' : 'none',
              visibility: activePanel === 'settings' ? 'visible' : 'hidden',
              opacity: activePanel === 'settings' ? '1' : '0'
            }}
          >
            <div 
              ref={settingsContainerRef}
              id="builderjs6-settings"
              className="builderjs6-settings-container"
              style={{
                display: activePanel === 'settings' ? 'block' : 'none',
                visibility: activePanel === 'settings' ? 'visible' : 'hidden',
                width: '100%',
                height: '100%',
                overflow: 'auto',
                opacity: activePanel === 'settings' ? '1' : '0'
              }}
            ></div>
          </div>
        </div>
        )}
      </div>

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="builderjs6-save-modal-overlay" onClick={handleCancelSave}>
          <div className="builderjs6-save-modal" onClick={(e) => e.stopPropagation()}>
            <div className="builderjs6-save-modal-header">
              <h3>Save Template</h3>
              <button className="builderjs6-save-modal-close" onClick={handleCancelSave}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="builderjs6-save-modal-body">
              <label htmlFor="template-name-input">Template Name</label>
              <input
                id="template-name-input"
                type="text"
                className="builderjs6-save-modal-input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmSave();
                  } else if (e.key === 'Escape') {
                    handleCancelSave();
                  }
                }}
              />
            </div>
            <div className="builderjs6-save-modal-footer">
              <button className="builderjs6-save-modal-btn cancel" onClick={handleCancelSave}>
                Cancel
              </button>
              <button className="builderjs6-save-modal-btn save" onClick={handleConfirmSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderJS6;

